import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { AlertController, PopoverController } from "ionic-angular";
import moment from "moment";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { PatientBenefitsService } from "providers/patient-charts/patient-benefits.service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { UtilityService } from "providers/utility-service/utility-service";
import { Observable, Subscription } from "rxjs";
import { commonVar } from "../../../../providers/common-var/common";
import { FacilitiesService } from "../../../../providers/facilities-service/facilities.service";
import { PatientChartService } from "../../../../providers/patient-charts/patient-chart.service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";
import {
  Config,
  PatientBenefitActionEvent,
  PatientBenefits,
  PayerConfig,
  payerTypeList,
  Plan,
  relationshipList,
  stateList,
  SubscriberHistory,
} from "../models";

const PRIMARY = "PRIMARY";
const SECONDARY = "SECONDARY";
const ROOMANDBOARD = "ROOMANDBOARD";
const HOSPICE = "HOSPICE";
const PALLIATIVE = "PALLIATIVE";

const MSG_PALLIATIVE_MEDICAID_PAYER_TYPE_CHANGED = `Please note.  You may record this payer, but the system currently does not support Medicaid billing.  If you need to enter Room & Board benefits, please use the link for "Room and Board"`;

@Component({
  selector: "patient-benefits-form",
  templateUrl: "patient-benefits-form.html",
})
export class PatientBenefitsFormComponent implements OnInit, OnDestroy {
  @Input() patientId: number;
  @Input() patientChartId: number;
  @Input() editMode: boolean;
  @Input() patientBenefitsList: PatientBenefits[];
  @Input() mostRecentPrimaryBenefit: PatientBenefits;
  @Input() mostRecentSecondaryBenefit: PatientBenefits;
  @Input() patientBenefitActionEvent: PatientBenefitActionEvent;
  @Input() roomAndBoardEnable: boolean;
  @Input() billingRoomAndBoardEnable: boolean;
  @Output() showForm = new EventEmitter<boolean>();
  @Output() reloadGrid = new EventEmitter<boolean>();

  payerLevelList: Config[] = [
    { name: "Primary", value: PRIMARY, disabled: false },
  ];

  billRate: Config[] = [
    { name: "Bill at Facility Rate", value: "FACILITY", disabled: false },
    {
      name: "Bill at Payer Room and Board Rate",
      value: "PAYER",
      disabled: false,
    },
  ];
  careLevels: any[] = [
    { name: "Level 1 ", level: "1" },
    { name: "Level 2 ", level: "2" },
    { name: "Level 3 ", level: "3" },
    { name: "Level 4 ", level: "4" },
    { name: "Level 5 ", level: "5" },
  ];

  liabilityList: any[] = [
    { name: "Yes", value: true },
    { name: "No", value: false },
  ];

  payerTypeList = payerTypeList;
  relationshipList = relationshipList;
  stateList = stateList;

  payerNameList: PayerConfig[] = null;
  planList: Plan[] = [];

  careType: string;
  currentPatientBenefit: PatientBenefits = null;

  // The reason control renders where the election fields do. The read-back PANEL lives on
  // patient-benefits-row instead: F1.S13's reader is reading, not editing, and making
  // someone open an edit form to read history is the wrong posture on a patient chart.
  get showElectionHistory(): boolean {
    return this.careType === "HOSPICE";
  }

  // ---------------------------------------------------------------------------
  // CO-2315 item #11 / D-4 - the reason control on the election fields.
  //
  // AC-021-1 asks the audit to capture a reason, and F1.S7 / F1.S13 both read one back.
  // Measured 2026-09-06, `grep -c reasonForPayerChange clinical-ui/src` was 0: no control
  // has ever written one, so every entry in dev carries "". The capture existed and the
  // way to fill it did not.
  //
  // Copied from the shipped claim-error override control (CO-3824,
  // pages/billing/modals/override-errors) - stacked label, character count, translated
  // string - rather than invented.
  // ---------------------------------------------------------------------------

  electionChangeReason: string = "";
  readonly maxElectionReasonLength: number = 500;
  private electionFieldsAtLoad: { election: string; notice: string } = {
    election: "",
    notice: "",
  };

  get electionReasonLength(): number {
    return (this.electionChangeReason || "").length;
  }

  // captureElectionFieldsAtLoad remembers what the fields held when the benefit loaded, so
  // "has the biller changed one" is answerable without asking the server. Called from the
  // same places the benefit is loaded.
  captureElectionFieldsAtLoad(): void {
    const group =
      this.patientBenefitForm && this.patientBenefitForm.get("hospiceEligibility");
    const value = group ? group.value || {} : {};
    this.electionFieldsAtLoad = {
      election: String(value.hospiceBenefitElectionDate || ""),
      notice: String(value.noticeAcceptedDate || ""),
    };
    this.electionChangeReason = "";
  }

  // hasElectionFieldChange drives whether the reason control is offered at all. Asking for
  // a reason on a save that did not touch either field would be asking about nothing.
  get hasElectionFieldChange(): boolean {
    const group =
      this.patientBenefitForm && this.patientBenefitForm.get("hospiceEligibility");
    if (!group) {
      return false;
    }
    const value = group.value || {};
    return (
      String(value.hospiceBenefitElectionDate || "") !==
        this.electionFieldsAtLoad.election ||
      String(value.noticeAcceptedDate || "") !== this.electionFieldsAtLoad.notice
    );
  }

  // The control renders only where the fields do, and only once one of them has changed.
  get showElectionReason(): boolean {
    return this.showElectionHistory && this.hasElectionFieldChange;
  }

  // applyElectionChangeReason decorates the outgoing payload with the biller's reason.
  //
  // A separate method purely so a spec can exercise the REAL decision rather than restate
  // it. submitForm() needs most of this screen's service graph to run, and a spec that
  // reimplements the condition would pass while production diverged from it - which is the
  // exact shape of bug this control exists to fix.
  applyElectionChangeReason(formData: any): any {
    if (!formData) {
      return formData;
    }
    if (this.hasElectionFieldChange && (this.electionChangeReason || "").trim()) {
      formData.reasonForPayerChange = this.electionChangeReason.trim();
    }
    return formData;
  }

  selectedPayerName: Config = null;
  selectedRelationship: Config = null;
  selectedState: Config = null;
  selectedPlanState: Config = null;

  payerNameOptions: Config[] = null;
  stateOptions: Config[];

  partialEdit: boolean = false;
  impactToClaim: boolean = false;
  subscriptions: Subscription[] = [];
  addNewPlan: boolean = false;
  duplicatedSubscriberId: boolean = false;

  patientBenefitForm: FormGroup;
  subscriberHistoryForm: FormGroup;
  billingDetails: FormGroup;

  phoneMask = commonVar.SIMPLE_PHONE_MASK;
  zipCodeMask = commonVar.ZIP_CODE_MASK;
  zipCodeExtMask = commonVar.ZIP_CODE_EXTENSION_MASK;
  locationTypes = commonVar.LOCATION_TYPES;
  locationTypeValues = commonVar.LOCATION_TYPE_VALUES;
  currentLocations = commonVar.CURRENT_LOCATIONS;
  ssnRegex = commonVar.SSN_REGEX;
  phoneRegex = commonVar.PHONE_REGEX;
  emailRegex = commonVar.EMAIL_REGEX;
  zipCodeExtensionMask = commonVar.ZIP_CODE_EXTENSION_MASK;
  lookUpConst = commonVar.LOOK_UP_CONST;
  subscriberIdPattern = commonVar.SUBSCRIBER_NUMBER_REGEX;
  medicareNumberPattern =
    /^[0-9][a-zA-Z][a-zA-Z0-9][0-9][a-zA-Z][a-zA-Z0-9][0-9][a-zA-Z]{2}[0-9]{2}$/i; //added valdation check for medicare nubmer
  phoneNumberPattern = commonVar.SIMPLE_PHONE_REGEX;

  zipPattern = commonVar.ZIP_PATTERN;
  zipExtPattern = commonVar.ZIP_EXT_PATTERN;
  zipWithExtPattern = new RegExp("^[0-9]{5}(-[0-9]{4})?$"); //"^\\d{5}(-{0,1}\\d{4})?$" //do not remove this line

  tenantId: number;

  patientStatus: string;
  minDateValue: any = { year: 1900, month: 1, day: 1 };
  maxDateValue: any = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
  };
  // NOVA-2061: a ceiling for fields that legitimately accept a FUTURE date, where
  // maxDateValue (today) would be wrong. Its job is not to restrict — ten years out
  // is far past any real payer effective date — it is to stop ng-bootstrap 1.1.2
  // recomputing the year list off the focused date
  // (`end = maxDate && maxDate.year || date.year + 10`), which made the list
  // collapse to a 10-year window as soon as the user navigated back.
  maxFutureDateValue: any = {
    year: new Date().getFullYear() + 10,
    month: 12,
    day: 31,
  };
  datePlacementPreference: PlacementArray = ["top"];
  selectedPayerLevel: Config;
  selectedPayerType: Config;

  patientChart: any = null;

  dateOverlapMessage: string =
    "You have an existing payer with the same or a previous start date. Please edit the existing payer or choose a different start date.";
  dateGapMessage: string =
    "Moving the Effective date will introduce a gap in your Primary payer record.";
  changeEffectiveDateMessage: string =
    "Modifying the payer's effective date could potentially affect the subscriber's historical information.";
  highDaysUsedWarningMessage: string = null;
  patientEligibilityVerified: boolean = false;
  isBVID: boolean = false;
  medicaidPending: boolean = false;
  medicaidPendingDisabled: boolean = false;
  disableExpiredDate: boolean = false;
  success: boolean = false;
  dateFormat: string = "YYYY-MM-DD";

  subscriberIdMinDateValue: any = { year: 1900, month: 1, day: 1 };

  dateRegex = new RegExp("\\d{4}-\\d{2}-\\d{2}");

  private medicareSyncSub: Subscription;

  // CO-3396 (A.3.11–A.3.14): flag-gated monthly state-Medicaid eligibility
  // (Aid Code + Share of Cost). Shown only for Medicaid benefits
  // (A.3.12); state-neutral labels/model (A.3.13). Server-side merge +
  // audit in patient-benefits-functions is authoritative.
  californiaMediCalSupportToggle: boolean = false;
  monthlyEligibilityHistory: any[] = [];
  currentEligibilityMonth: string = moment().format("YYYY-MM");

  // CO-2315 item #5 (F9.S4): the maintained Medi-Cal Aid Code reference table,
  // keyed by code, so the description can be shown beside the field as the biller
  // types. The map is EXACT-MATCH, mirroring AidCodeTable.Lookup on the server:
  // DHCS codes are recorded as DHCS writes them, leading zero included, so "13"
  // and "13 " and "1" are three different strings and only one of them is a code.
  // Normalising here would make this screen disagree with the warning the server
  // returns for the same keystroke.
  aidCodeDescriptions: { [code: string]: string } = {};

  // The description currently shown beside the Aid Code field. Empty for a code
  // that is not in the table — F9.S4 asks for a description, and the unrecognized
  // code is F9.S6's business, which fires on SAVE. Warning per keystroke would
  // fire on "1" on the way to "13", which is how a soft warning stops being read.
  aidCodeDescription: string = "";

  // CO-3398 (A.4.1/A.4.2): per-state SOC code identifiers, as DATA — a new
  // state adds an entry here (or a config source later), never a branch.
  static readonly STATE_SOC_CODES: { [state: string]: any[] } = {
    CA: [
      { codeType: "occurrenceSpanCode", code: "22" },
      { codeType: "valueCode", code: "23" },
    ],
  };

  // CO-3618 (P4, money correctness): convert a biller-typed DOLLAR amount into
  // integer CENTS for storage. patient-benefits-functions stores
  // MonthlyEligibility.shareOfCostMonthly as money.Money (integer cents), so
  // $300 must go over the wire as 30000, not 300. The prior code sent
  // Number(soc) (raw dollars): a whole number ($300, or $55.00 which JS
  // collapses to 55) serialized as a JSON integer and was stored 100x under;
  // only a genuine non-integer (55.50) serialized as a JSON float and happened
  // to convert. Always multiply by 100 and round to the nearest cent, and
  // null-guard null / undefined / empty / whitespace / NaN so a bad input
  // never becomes a bogus amount.
  static dollarsToCents(value: any): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === "string" && value.trim() === "") {
      return null;
    }
    const dollars = Number(value);
    if (isNaN(dollars)) {
      return null;
    }
    return Math.round(dollars * 100);
  }

  // CO-3690 (money correctness, the load half): the inverse of dollarsToCents.
  // patient-benefits-functions returns MonthlyEligibility.shareOfCostMonthly as
  // integer CENTS, but the form input is a DOLLAR field and save runs
  // dollarsToCents() over whatever it holds. CO-3618 fixed the save direction but
  // loaded the raw cents straight into the dollar field, so an existing $300 SOC
  // rendered as "30000" and a re-save without editing ran dollarsToCents(30000) =
  // $30,000 — a 100x INFLATION mirroring the original 100x understatement. Convert
  // cents -> dollars on load so the field round-trips. Null/NaN guarded.
  static centsToDollars(value: any): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === "string" && value.trim() === "") {
      return null;
    }
    const cents = Number(value);
    if (isNaN(cents)) {
      return null;
    }
    return cents / 100;
  }

  get showMonthlyEligibility() {
    return (
      this.californiaMediCalSupportToggle &&
      this.patientBenefitForm &&
      this.patientBenefitForm.get("payerType").value === "MEDICAID"
    );
  }

  /**
   * CO-2315 Round-2 item #7, decision D3.
   *
   * "Bill at Payer Room and Board Rate" is not a permitted choice for a Medi-Cal patient: the
   * facility's effective-dated rate is the sole room-and-board pricing source (Payer
   * Foundation F5.S3, R-2 ruling). This selector is the ENTRY POINT for that mistake. Removing
   * the payer Rates tab field stops a rate being *defined*; it does nothing to stop a biller
   * *choosing* Payer here and meeting the block at claim time, days later, with the claim
   * parked and nobody watching the DLQ.
   *
   * Disabled, not hidden, following the R-5 precedent for the override control: a biller
   * looking at a benefit that was saved as Payer needs to see what it says and why it can no
   * longer be chosen. Hiding the option would make an existing stored value unexplainable.
   *
   * BOTH halves of the condition matter. F5.S3 says "Medicaid-classified payers"; implemented
   * literally, this would grey the option out for every Ohio, CareSource, Molina, Anthem, UHC
   * Community Plan, Wellpoint, Wyoming, Indiana and South Carolina Medicaid patient — 1,021
   * live benefits across 8 tenants that are billing off exactly this setting today (measured
   * across all 45,145 production benefit rows, 2026-09-04; none Californian). The tenant's
   * Medi-Cal enablement is what scopes it, and it is the same predicate the backend applies
   * (revenue-line-item-functions IsMediCalRBPayerRateBlocked). The screen and the pricing path
   * must agree, or a biller is refused a choice the backend would have honoured, or offered
   * one it will refuse.
   */
  get isPayerBillRateBlocked(): boolean {
    if (!this.californiaMediCalSupportToggle || !this.patientBenefitForm) {
      return false;
    }
    const payerType = this.patientBenefitForm.get("payerType");
    const isMedicaid =
      !!payerType &&
      String(payerType.value || "")
        .trim()
        .toUpperCase() === "MEDICAID";
    if (!isMedicaid) {
      return false;
    }
    // PRD 2 REQ-002's condition is `PayerType = Medicaid AND state = CA`, and the pricing
    // path enforces all three conditions. This control must agree with it, or a biller is
    // refused a choice the backend would have honoured - or offered one it will refuse.
    return this.selectedPayerState().trim().toUpperCase() === "CA";
  }

  /**
   * The selected payer's state, resolved from the payer catalogue.
   *
   * Same lookup CO-3398 already uses to resolve the Share-of-Cost state codes further down
   * this file - one pattern, not two. Returns "" when the catalogue has not loaded or the
   * payer carries no state, which reads as "not a Medi-Cal payer": 453 of 453 Medicaid payer
   * records in production carry no state today, so this is the common case, not the edge.
   */
  private selectedPayerState(): string {
    if (!this.patientBenefitForm) {
      return "";
    }
    const payerIdControl = this.patientBenefitForm.get("payerId");
    const payerId = payerIdControl ? payerIdControl.value : null;
    const payer = (this.payerNameList || []).find((p) => p.payerId === payerId);
    return payer && payer.state ? String(payer.state) : "";
  }

  /**
   * True when this billing-detail row was SAVED as Payer and is now blocked — a benefit
   * entered before the tenant was enabled for Medi-Cal, or before this rule shipped.
   *
   * These rows are not hypothetical and they are the reason the option is disabled rather
   * than hidden. The row keeps its stored value (silently rewriting a biller's saved billing
   * instruction is not ours to do), and the affordance tells them it must be changed before
   * the claim will price.
   */
  isStoredPayerBillRateNowBlocked(billingDetail: AbstractControl): boolean {
    if (!this.isPayerBillRateBlocked || !billingDetail) {
      return false;
    }
    const billRate = billingDetail.get("billRate");
    // Explicit boolean, not a && chain. A chain returns the first falsy OPERAND — null for a
    // row with no billRate control — so the declared `boolean` would be a lie and any caller
    // doing a strict comparison would silently disagree with the template's *ngIf.
    return !!billRate && billRate.value === "PAYER";
  }

  /**
   * Marks the Payer option disabled or enabled to match isPayerBillRateBlocked.
   *
   * Mutates the existing option objects rather than rebuilding the array, following the
   * pattern already used for payerLevelList further down this file. A getter returning a
   * fresh array would hand ng-select a new identity on every change-detection pass, and this
   * component runs the Default strategy.
   */
  applyBillRateAvailability(): void {
    const blocked = this.isPayerBillRateBlocked;
    this.billRate.forEach((option) => {
      if (option.value === "PAYER") {
        option.disabled = blocked;
      }
    });
  }

  constructor(
    private fb: FormBuilder,
    private popover: PopoverController,
    private referralsService: ReferralsServiceProvider,
    private patientBenefitsService: PatientBenefitsService,
    private navBarService: NavBarServiceProvider,
    private utilService: UtilityService,
    private patientChartService: PatientChartService,
    private facilityService: FacilitiesService,
    private alertCtrl: AlertController,
    private stateManagementService: StateManagementService
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.stateManagementService.californiaMediCalSupportToggle.subscribe(
        (enabled) => {
          this.californiaMediCalSupportToggle = !!enabled;
          // The flag arrives asynchronously and usually AFTER the form is built, so the
          // option list has to be re-evaluated here as well as on payer-type change.
          // Without this the very first render of an already-Medicaid benefit shows Payer
          // selectable, which is the render a biller actually sees.
          this.applyBillRateAvailability();
        }
      )
    );
    this.retrieveCareLevels();
    this.retrieveConfig();
    this.loadAidCodeReference();
    this.impactToClaim = false;
  }

  /**
   * CO-2315 item #5 (F9.S4 / C1, C2): load the Aid Code reference table and start
   * tracking the field.
   *
   * The service caches the rows for the session, so this costs one request per
   * signed-in user however many times the form is opened. It is called
   * unconditionally rather than behind showMonthlyEligibility because that getter
   * depends on a payer type the biller may change after the form has loaded, and a
   * table fetched on a value change would arrive after the keystroke that needed it.
   */
  loadAidCodeReference(): void {
    this.subscriptions.push(
      this.patientBenefitsService.getAidCodes().subscribe((entries) => {
        const byCode: { [code: string]: string } = {};
        (entries || []).forEach((entry) => {
          if (entry && entry.code) {
            byCode[entry.code] = entry.description || "";
          }
        });
        this.aidCodeDescriptions = byCode;
        // The table may land after the biller has already typed — resolve what is
        // in the field now rather than waiting for the next keystroke.
        this.refreshAidCodeDescription();
      })
    );

    const aidCode = this.patientBenefitForm
      ? this.patientBenefitForm.get("aidCode")
      : null;
    if (aidCode) {
      this.subscriptions.push(
        aidCode.valueChanges.subscribe(() => this.refreshAidCodeDescription())
      );
    }
  }

  private refreshAidCodeDescription(): void {
    const control = this.patientBenefitForm
      ? this.patientBenefitForm.get("aidCode")
      : null;
    const code = control ? control.value : "";
    this.aidCodeDescription = this.aidCodeDescriptions[code] || "";
  }

  /**
   * CO-2315 item #5 (F9.S6): the message shown when a save completes.
   *
   * "the non-blocking soft warning ... is displayed, the save still completes, and
   * the entered code is retained." So the confirmation the biller already gets is
   * kept and the warning is appended to it — replacing it would tell her the save
   * had failed, which is the one thing the scenario says must not happen.
   *
   * THE WORDING IS THE SERVER'S, VERBATIM. Composing it here would put a second
   * copy of a Product-ruled string in a second language, which is exactly how all
   * four Aid Code warnings drifted from their approved wording and stayed wrong
   * until somebody diffed them by hand.
   */
  saveMessageFor(successMsg: string, response: any): string {
    const warnings =
      response && response.aidCodeWarnings ? response.aidCodeWarnings : [];
    if (!warnings.length) {
      return successMsg;
    }
    return (
      successMsg + "<br><br>" + warnings.map((w) => w && w.message).join("<br>")
    );
  }

  ngOnDestroy(): void {
    if (this.medicareSyncSub) {
      this.medicareSyncSub.unsubscribe();
    }
    this.subscriptions.forEach((val) => val.unsubscribe());
  }

  retrieveConfig() {
    const msg = "Failed to retrieve Payers, please retry.";
    this.showLoadingMessage();
    this.patientBenefitsService.getPayerNames().subscribe(
      (response) => {
        // Determine the care type - use patientBenefitActionEvent.patientBenefit.benefitType if CARE_TYPE is undefined
        let careType = this.referralsService.CARE_TYPE;
        if (
          !careType &&
          this.editMode &&
          this.patientBenefitActionEvent &&
          this.patientBenefitActionEvent.patientBenefit &&
          this.patientBenefitActionEvent.patientBenefit.benefitType
        ) {
          careType = this.patientBenefitActionEvent.patientBenefit.benefitType;
        }

        const careTypeUpper = String(careType).toUpperCase();

        if (response != null) {
          this.payerNameList = response
            .filter((p) => p.benefitType.includes(careTypeUpper))
            .sort((a, b) => a.payerName.localeCompare(b.payerName));
          if (careTypeUpper === HOSPICE) {
            this.payerNameList = [
              ...this.payerNameList,
              ...response.filter((p) => p.benefitType === ROOMANDBOARD),
            ].sort((a, b) => a.payerName.localeCompare(b.payerName));
          }

          this.payerNameList.forEach((p) => {
            if (p.plans && p.plans.length > 0) {
              p.plans.forEach((pa) => {
                let zipExt =
                  pa.zipCodeExtension === "" || pa.zipCodeExtension === "-"
                    ? ""
                    : " - " + pa.zipCodeExtension;
                pa.value =
                  pa.planName +
                  " - " +
                  pa.address1 +
                  ", " +
                  pa.city +
                  ", " +
                  pa.state +
                  " " +
                  pa.zipCode +
                  zipExt;
              });
            } else {
              p.plans = [];
            }

            p.plans.unshift(this.inserPlanToPayerConfig("Add New"));
            p.plans.unshift(this.inserPlanToPayerConfig("-"));
          });

          this.loadProperties();
        } else {
          this.onFailure(msg);
        }
      },
      () => {
        this.onFailure(msg);
      }
    );
  }

  retrieveCareLevels() {
    this.facilityService.getCareLevels().subscribe(
      (response) => {
        if (response != null && response.length > 0) {
          this.careLevels = [];
          response.forEach((careLevel) => {
            this.careLevels.push({
              name:
                careLevel.name +
                (careLevel.description.trim().length > 0
                  ? " - " + careLevel.description
                  : ""),
              level: careLevel.level.toString(),
            });
          });
        }
      },
      () => {
        this.onFailure("Failed to retrieve Care Levels, please retry.");
      }
    );
  }

  inserPlanToPayerConfig(value: string): Plan {
    return {
      planName: value,
      newPlanName: "",
      address1: "",
      city: "",
      state: "",
      zipCode: "",
      zipCodeExtension: "",
      value: value,
    };
  }

  loadProperties() {
    this.mapOptionsFromList();
    this.tenantId = +this.referralsService.TENANT_ID;
    this.careType = String(this.referralsService.CARE_TYPE).toUpperCase();
    this.patientStatus = String(
      this.referralsService.PATIENT_STATUS
    ).toUpperCase();

    this.addSecondaryPayerLevel();
    this.initPatientBenefitForm();
    this.verifyAndCopyBenefit();
    this.verifyAndEditBenefit();

    if (this.careType === HOSPICE) {
      this.getPatientChart(false);
      if (this.roomAndBoardEnable) {
        this.addRoomAndBoardPayerLevel();
      }
    }
    this.utilService.decreaseSpinnerCount();

    console.log("mostRecentPrimaryBenefit: ", this.mostRecentPrimaryBenefit);
    console.log("mostRecentSecondaryBenefit", this.mostRecentSecondaryBenefit);
    console.log("patientStatus: ", this.patientStatus);
  }

  private verifyAndEditBenefit() {
    if (
      this.patientBenefitActionEvent &&
      this.patientBenefitActionEvent.action === "edit" &&
      this.editMode
    ) {
      this.showLoadingMessage();
      this.getCurrentBenefit().subscribe(
        (response) => {
          if (!response) {
            this.decreaseSpinnerAndShowFailure();
            return;
          }
          this.currentPatientBenefit = response;
          this.captureElectionFieldsAtLoad();
          this.checkPartialEditMode(response);
          this.mapCurrentPatientBenefitToForm();
          this.impactToClaim = false;
          this.utilService.decreaseSpinnerCount();
          this.isBVID = response.subscriber.isVBID;
          this.medicaidPending = response.medicaidPending;
          this.setMedicaidPendingDisabled(
            this.patientBenefitForm.get("medicaidNumber").value
          );
          let subscriberIdRegex = new RegExp(this.subscriberIdPattern);
          if (
            response.payerType === "MEDICARE" &&
            response.subscriber.subscriberId &&
            !subscriberIdRegex.test(response.subscriber.subscriberId)
          ) {
            this.isBVID = true;
            this.updateIsVBID();
          }
        },
        (_err) => {
          this.decreaseSpinnerAndShowFailure();
        }
      );
    }
  }

  private verifyAndCopyBenefit() {
    if (
      this.patientBenefitActionEvent &&
      this.patientBenefitActionEvent.action === "copy"
    ) {
      this.showLoadingMessage();
      this.getCurrentBenefit().subscribe(
        (response) => {
          if (!response) {
            this.decreaseSpinnerAndShowFailure();
            return;
          }
          this.currentPatientBenefit = response;
          this.captureElectionFieldsAtLoad();
          this.resetCopyOfCurrentPatientBenefit();
          this.mapCurrentPatientBenefitToForm();
          this.utilService.decreaseSpinnerCount();
        },
        (_err) => {
          this.decreaseSpinnerAndShowFailure();
        }
      );
    }
  }

  private showLoadingMessage() {
    this.utilService.increaseSpinnerCount("Loading details...");
  }

  private decreaseSpinnerAndShowFailure(msg?: string) {
    this.utilService.decreaseSpinnerCount();
    if (!msg) {
      msg = "Failed to retrieve Benefit information, please retry.";
    }
    this.onFailure(msg);
  }

  private getCurrentBenefit(): Observable<PatientBenefits> {
    return this.patientBenefitsService.getPatientBenefitFromId(
      this.patientBenefitActionEvent.patientBenefit.patientChartId,
      this.patientBenefitActionEvent.patientBenefit.benefitsId
    );
  }

  private resetCopyOfCurrentPatientBenefit() {
    if (!this.currentPatientBenefit) {
      return;
    }
    this.currentPatientBenefit.authorizations = [];
    this.currentPatientBenefit.benefitsId = "";
    this.currentPatientBenefit.createdDatetime = "";
    this.currentPatientBenefit.effectiveDate = "";
    this.currentPatientBenefit.expiredDate = "";
    this.currentPatientBenefit.updatedDatetime = "";
    this.currentPatientBenefit.readyToBill = "No";
    this.currentPatientBenefit.status = "ACTIVE";
    this.currentPatientBenefit.reasonForHold = null;
    this.impactToClaim = false;
  }

  private addSecondaryPayerLevel() {
    if (
      this.mostRecentPrimaryBenefit &&
      (!this.editMode ||
        (this.patientBenefitActionEvent.patientBenefit &&
          this.patientBenefitActionEvent.patientBenefit.payerPrimacyLevel !==
            PRIMARY))
    ) {
      this.payerLevelList.push({
        name: "Secondary",
        value: SECONDARY,
        disabled: false,
      });
    } else if (
      this.editMode &&
      this.patientBenefitActionEvent.patientBenefit &&
      this.patientBenefitActionEvent.patientBenefit.payerPrimacyLevel ===
        SECONDARY
    ) {
      this.payerLevelList.push({
        name: "Secondary",
        value: SECONDARY,
        disabled: false,
      });
    }
  }

  private addRoomAndBoardPayerLevel() {
    if (
      this.mostRecentPrimaryBenefit &&
      (!this.editMode ||
        (this.patientBenefitActionEvent.patientBenefit &&
          this.patientBenefitActionEvent.patientBenefit.payerPrimacyLevel !==
            PRIMARY))
    ) {
      this.payerLevelList.push({
        name: "Room And Board",
        value: ROOMANDBOARD,
        disabled: false,
      });
    } else if (
      this.editMode &&
      this.patientBenefitActionEvent.patientBenefit &&
      this.patientBenefitActionEvent.patientBenefit.payerPrimacyLevel ===
        ROOMANDBOARD
    ) {
      this.payerLevelList.push({
        name: "Room And Board",
        value: ROOMANDBOARD,
        disabled: false,
      });
    }
  }
  private setupMedicareRealtimeSync() {
    // Unsubscribe previous
    if (this.medicareSyncSub) {
      this.medicareSyncSub.unsubscribe();
    }
    // Only if payer type is MEDICARE and at least one subscriber
    if (
      this.patientBenefitForm.get("payerType").value === "MEDICARE" &&
      this.subscriberHistory.length > 0
    ) {
      const firstSubscriber = this.subscriberHistory.at(0);
      this.medicareSyncSub = firstSubscriber
        .get("subscriberId")
        .valueChanges.subscribe((val) => {
          this.patientBenefitForm
            .get("medicareNumber")
            .setValue(val ? val.toUpperCase() : "", { emitEvent: false });
        });
      const val = firstSubscriber.get("subscriberId").value;
      this.patientBenefitForm
        .get("medicareNumber")
        .setValue(val ? val.toUpperCase() : "", { emitEvent: false });
    }
  }

  mapOptionsFromList() {
    this.stateOptions = this.stateList.map((s) => {
      return {
        name: s,
        value: s,
        disabled: false,
      };
    });

    this.payerNameOptions = this.payerNameList.map((p) => {
      return {
        name: p.payerName,
        value: p.payerId,
        disabled: false,
      };
    });
  }

  initPatientBenefitForm() {
    this.patientBenefitForm = this.fb.group({
      benefitsId: [null],
      benefitsVersion: [1],
      benefitType: [this.careType],

      activePrimaryBenefitsId: [null],
      expiredActivePrimaryBenefitDate: [null],

      tenantId: [this.tenantId],
      patientId: [this.patientId],
      patientChartId: [this.patientChartId],

      payerPrimacyLevel: ["", Validators.required],

      payerType: ["", Validators.required],
      payerId: ["", Validators.required],
      payerName: ["", Validators.required],

      addNewPayerPlan: [false],
      eligibilityVerified: [null],
      plan: this.fb.group({
        planName: [""],
        newPlanName: [""],
        address1: [""],
        // address2: [''],
        city: [""],
        state: [""],
        zipCode: [
          "",
          Validators.compose([Validators.pattern(this.zipPattern)]),
        ],
        zipCodeExtension: [
          "",
          Validators.compose([Validators.pattern(this.zipExtPattern)]),
        ],
        // zipCode: ['', Validators.compose([Validators.pattern(this.zipPattern)])],
        // zipCodeExtension: ['', Validators.compose([Validators.pattern(this.zipExtPattern)])],
        value: [""],
      }),

      effectiveDate: ["", Validators.required],
      expiredDate: [""],
      readyToBill: ["No"],
      status: ["ACTIVE"],
      reasonForHold: [null],
      medicaidNumber: [""],
      aidCode: [""],
      shareOfCostMonthly: [null],
      medicareNumber: ["", Validators.pattern(this.medicareNumberPattern)],
      medicaidPending: false,

      subscriber: this.fb.group({
        relationship: [""],
        subscriberId: [""],
        groupNumber: ["", Validators.compose([Validators.maxLength(20)])],
        firstName: ["", Validators.compose([Validators.maxLength(75)])],
        lastName: ["", Validators.compose([Validators.maxLength(50)])],
        middleInitial: ["", Validators.maxLength(2)],
        dateOfBirth: [""],
        address1: ["", Validators.compose([Validators.maxLength(75)])],
        city: ["", Validators.compose([Validators.maxLength(50)])],
        state: [""],
        zipCode: [
          "",
          Validators.compose([Validators.pattern(this.zipPattern)]),
        ],
        zipCodeExtension: [
          "",
          Validators.compose([Validators.pattern(this.zipExtPattern)]),
        ],
        phoneNumber: [""],
        emailAddress: [
          "",
          [Validators.pattern(this.emailRegex), Validators.maxLength(50)],
        ],
        additionalInfo: [""],
        isVBID: false,
        subscriberHistory: this.fb.array([]),
        subscriberIdChange: false,
      }),

      financial: this.fb.group({
        coPay: [null],
        limitAmount: [null],
        deductible: [null],
        maxVisit: [null],
      }),

      hospiceEligibility: this.fb.group({
        routineHomeCareHighDaysUsed: [null],
        hospiceBenefitElectionDate: [""],
        admitBenefitPeriod: [null],
        admitBenefitPeriodStartDate: [""],
        // admitHospiceEpisode: [null],
        previousHospiceStartDate: [""],
        previousHospiceDischargeDate: [""],
        dateOfFinalBill: [""],
        noticeAcceptedDate: [""],
      }),

      roomAndBoardEligibility: this.fb.group({
        admitBenefitPeriod: null,
        admitBenefitPeriodStartDate: [""],
        patientLiability: this.fb.array([
          {
            patientLiability: false,
            liabilityAmount: 0,
            fromDate: "",
            toDate: "",
          },
        ]),
      }),

      authorizations: this.fb.array([]),
      billingDetails: this.fb.array([
        { start: "", end: "", billRate: "", careLevel: 0 },
      ]),
    });

    this.subscriberHistoryForm = this.fb.group({
      subscriberHistory: this.fb.array([]),
    });

    this.billingDetails = this.fb.group({
      billingDetails: this.fb.array([]),
    });

    this.addValueChangesSubscriptions();
    this.addSubscriberHistory();
    if (this.billingRoomAndBoardEnable) {
      this.addBillingEffectiveDate();
    }
    this.subscriberHistory.controls[0].get("subscriberId").clearValidators();
    this.subscriberHistory.controls[0].get("subscriberId").setErrors(null);
  }

  addValueChangesSubscriptions() {
    this.subscriptions.push(
      this.patientBenefitForm
        .get("payerType")
        .valueChanges.subscribe((value) => this.onPayerTypeChanges(value))
    );
    this.subscriptions.push(
      this.patientBenefitForm
        .get("payerPrimacyLevel")
        .valueChanges.subscribe((value) =>
          this.onPayerPrimacyLevelChanges(value)
        )
    );
    this.subscriptions.push(
      this.patientBenefitForm
        .get("effectiveDate")
        .valueChanges.subscribe((value) => this.onEffectiveDateChanges(value))
    );
    this.subscriptions.push(
      this.patientBenefitForm
        .get("expiredDate")
        .valueChanges.subscribe((value) => this.onExpiredDateChanges(value))
    );
    this.subscriptions.push(
      this.patientBenefitForm
        .get("subscriber")
        .get("relationship")
        .valueChanges.subscribe(() => this.changeImpactToClaim())
    );
    this.subscriptions.push(
      this.patientBenefitForm
        .get("subscriber")
        .get("phoneNumber")
        .valueChanges.subscribe(() => this.onPhoneNumberChanges())
    );
    this.subscriptions.push(
      this.patientBenefitForm
        .get("hospiceEligibility")
        .get("hospiceBenefitElectionDate")
        .valueChanges.subscribe(() => this.changeImpactToClaim())
    );
    this.subscriptions.push(
      this.patientBenefitForm
        .get("hospiceEligibility")
        .get("admitBenefitPeriod")
        .valueChanges.subscribe((value) =>
          this.onAdmitBenefitPeriodChanges(value)
        )
    );
    this.subscriptions.push(
      this.patientBenefitForm
        .get("hospiceEligibility")
        .get("admitBenefitPeriodStartDate")
        .valueChanges.subscribe((value) =>
          this.onAdmitBenefitPeriodStartDateChanges(value)
        )
    );
    this.subscriptions.push(
      this.patientBenefitForm
        .get("hospiceEligibility")
        .get("routineHomeCareHighDaysUsed")
        .valueChanges.subscribe(() => this.changeImpactToClaim())
    );
    this.subscriptions.push(
      this.patientBenefitForm
        .get("medicaidNumber")
        .valueChanges.subscribe((value) => this.onMedicaidNumberChanges(value))
    );
    // Real-time sync when payer type is MEDICARE, always show latest Subscriber ID in Medicare
    this.subscriptions.push(
      this.patientBenefitForm.get("payerType").valueChanges.subscribe(() => {
        this.setupMedicareRealtimeSync();
        // CO-2315 item #7 D3: switching a benefit to or from MEDICAID changes whether "Bill
        // at Payer Room and Board Rate" is an available choice.
        this.applyBillRateAvailability();
      })
    );
    this.setupMedicareRealtimeSync();
    this.applyBillRateAvailability();
  }

  onPayerTypeChanges(value) {
    this.selectedPayerName = null;

    this.patientBenefitForm.controls["payerId"].setValue(null);
    this.patientBenefitForm.controls["payerName"].setValue(null);
    // If Payer is medicare dosen't allows to edit medicare number and show latest subscriberID
    if (value === "MEDICARE") {
      if (this.subscriberHistory.controls.length > 0) {
        const latestSubscriberId =
          this.subscriberHistory.controls[0].get("subscriberId").value;
        this.patientBenefitForm
          .get("medicareNumber")
          .setValue(latestSubscriberId);
      } else {
        this.patientBenefitForm.get("medicareNumber").setValue("");
      }
      this.patientBenefitForm.get("medicareNumber").disable();
    } else {
      this.patientBenefitForm.get("medicareNumber").setValue("");
      this.patientBenefitForm.get("medicareNumber").enable();
    }
    if (this.careType === PALLIATIVE && value === "MEDICAID") {
      this.warningPopup(
        MSG_PALLIATIVE_MEDICAID_PAYER_TYPE_CHANGED,
        "payerType",
        value
      );
    }

    this.subscriberHistory.controls.forEach((s) => {
      if (value === "MEDICARE" && !this.isBVID) {
        s.get("subscriberId").setValidators(
          Validators.compose([
            Validators.pattern(this.subscriberIdPattern),
            Validators.maxLength(11),
          ])
        );
        s.get("subscriberId").updateValueAndValidity({ emitEvent: false });
      } else {
        s.get("subscriberId").clearValidators();
        s.get("subscriberId").setErrors(null);
      }
      s.get("subscriberId").updateValueAndValidity({ emitEvent: false });
    });

    this.clearMedicaidNumber();
    this.setMedicaidPendingDisabled(
      this.patientBenefitForm.get("medicaidNumber").value
    );
  }

  onPayerPrimacyLevelChanges(value) {
    if (
      value &&
      !this.partialEdit &&
      this.mostRecentPrimaryBenefit &&
      this.patientBenefitsList.filter((f) => f.payerPrimacyLevel === PRIMARY)
        .length >= 1 &&
      value === PRIMARY
    ) {
      if (
        this.patientBenefitsList.filter((f) => f.payerPrimacyLevel === PRIMARY)
          .length === 1
      ) {
        if (
          this.mostRecentPrimaryBenefit.benefitsId !==
          this.patientBenefitForm.get("benefitsId").value
        ) {
          if (
            moment(
              this.patientBenefitForm.get("effectiveDate").value
            ).isSameOrBefore(
              moment(this.mostRecentPrimaryBenefit.effectiveDate)
            )
          ) {
            this.warningPopup(this.dateOverlapMessage, "effectiveDate", "");
          } else if (
            this.editMode &&
            moment(this.patientBenefitForm.get("effectiveDate").value).isAfter(
              moment(this.mostRecentPrimaryBenefit.effectiveDate).add(1, "day")
            ) &&
            this.patientBenefitForm.value.payerPrimacyLevel !== SECONDARY
          ) {
            this.warningPopup(this.dateGapMessage, "effectiveDate", "");
          }
        }
      } else {
        if (
          moment(
            this.patientBenefitForm.get("effectiveDate").value
          ).isSameOrBefore(moment(this.mostRecentPrimaryBenefit.effectiveDate))
        ) {
          this.warningPopup(this.dateOverlapMessage, "effectiveDate", "");
        } else if (
          this.editMode &&
          moment(this.patientBenefitForm.get("effectiveDate").value).isAfter(
            moment(this.mostRecentPrimaryBenefit.effectiveDate).add(1, "day")
          ) &&
          this.patientBenefitForm.value.payerPrimacyLevel !== SECONDARY
        ) {
          this.warningPopup(this.dateGapMessage, "effectiveDate", "");
        }
      }
    } else if (
      value &&
      !this.partialEdit &&
      this.mostRecentSecondaryBenefit &&
      value === SECONDARY &&
      moment(this.patientBenefitForm.get("effectiveDate").value).isSameOrBefore(
        moment(this.mostRecentSecondaryBenefit.effectiveDate)
      )
    ) {
      this.warningPopup(this.dateOverlapMessage, "effectiveDate", "");
    }
    this.clearMedicaidNumber();
    this.setMedicaidPendingDisabled(
      this.patientBenefitForm.get("medicaidNumber").value
    );
  }

  onEffectiveDateChanges(value) {
    const previousValue = this.patientBenefitForm.value.effectiveDate;
    if (
      value &&
      !this.partialEdit &&
      this.mostRecentPrimaryBenefit &&
      this.patientBenefitsList.filter((f) => f.payerPrimacyLevel === PRIMARY)
        .length >= 1 &&
      this.patientBenefitForm.get("payerPrimacyLevel").value === PRIMARY
    ) {
      if (
        this.patientBenefitsList.filter((f) => f.payerPrimacyLevel === PRIMARY)
          .length === 1
      ) {
        if (
          this.mostRecentPrimaryBenefit.benefitsId !==
          this.patientBenefitForm.get("benefitsId").value
        ) {
          if (
            moment(value).isSameOrBefore(
              moment(this.mostRecentPrimaryBenefit.effectiveDate)
            )
          ) {
            this.warningPopup(
              this.dateOverlapMessage,
              "effectiveDate",
              previousValue
            );
          } else if (
            this.editMode &&
            moment(value).isAfter(
              moment(this.mostRecentPrimaryBenefit.effectiveDate).add(1, "day")
            )
          ) {
            this.warningPopup(
              this.dateGapMessage,
              "effectiveDate",
              previousValue
            );
          } else {
            this.changeImpactToClaim();
          }
        }
      } else {
        if (
          moment(value).isSameOrBefore(
            moment(this.mostRecentPrimaryBenefit.effectiveDate)
          )
        ) {
          this.warningPopup(
            this.dateOverlapMessage,
            "effectiveDate",
            previousValue
          );
        } else if (
          this.editMode &&
          moment(value).isAfter(
            moment(this.mostRecentPrimaryBenefit.effectiveDate).add(1, "day")
          )
        ) {
          this.warningPopup(
            this.dateGapMessage,
            "effectiveDate",
            previousValue
          );
        } else {
          this.changeImpactToClaim();
        }
      }
    } else if (
      value &&
      !this.partialEdit &&
      this.mostRecentSecondaryBenefit &&
      this.patientBenefitForm.get("payerPrimacyLevel").value === SECONDARY &&
      moment(value).isSameOrBefore(
        moment(this.mostRecentSecondaryBenefit.effectiveDate)
      )
    ) {
      this.warningPopup(
        this.dateOverlapMessage,
        "effectiveDate",
        previousValue
      );
    } else {
      this.changeImpactToClaim();
    }

    if (
      value &&
      !this.partialEdit &&
      value !== previousValue &&
      this.patientBenefitForm.get("payerPrimacyLevel").value === SECONDARY
    ) {
      this.patientBenefitForm
        .get("expiredDate")
        .setValue("", { emitEvent: false });
      this.changeImpactToClaim();
    }

    if (
      value !== previousValue &&
      this.subscriberHistory &&
      this.subscriberHistory.controls.length > 0
    ) {
      if (this.subscriberHistory.controls.length == 1) {
        this.subscriberHistory.controls[
          this.subscriberHistory.controls.length - 1
        ]
          .get("effectiveDate")
          .setValue(value);
      } else if (value) {
        this.subscriberIdEffectiveDateChangeWarning(
          this.changeEffectiveDateMessage,
          "effectiveDate",
          value,
          previousValue
        );
      }
    }
  }

  subscriberIdEffectiveDateChangeWarning(
    message: string,
    field: string,
    value: string,
    previousValue
  ) {
    const alert = this.alertCtrl.create({
      enableBackdropDismiss: false,
      title: "Warning Message",
      message: message,
      buttons: [
        {
          text: "Proceed",
          handler: () => {
            let lengthBeforeFilter = 0;
            do {
              this.subscriberHistory.controls[
                this.subscriberHistory.controls.length - 1
              ]
                .get("effectiveDate")
                .setValue(value);
              this.updateSubscriberIdDates();
              lengthBeforeFilter = this.subscriberHistory.controls.length;
              this.subscriberHistory.controls =
                this.subscriberHistory.controls.filter((s) => {
                  return s.get("expiredDate").value == "-"
                    ? true
                    : !moment(s.get("effectiveDate").value).isAfter(
                        moment(s.get("expiredDate").value)
                      );
                });
            } while (
              lengthBeforeFilter > this.subscriberHistory.controls.length
            );
          },
        },
        {
          text: "Cancel",
          handler: () => {
            this.patientBenefitForm
              .get(field)
              .setValue(previousValue, { emitEvent: false });
          },
        },
      ],
    });

    alert.present();
  }

  onExpiredDateChanges(value) {
    const previousValue = this.patientBenefitForm.value.expiredDate;
    if (
      value &&
      (this.patientBenefitForm.get("payerPrimacyLevel").value === SECONDARY ||
        this.patientBenefitForm.get("payerPrimacyLevel").value ===
          ROOMANDBOARD) &&
      this.patientBenefitForm.get("effectiveDate").value !== ""
    ) {
      if (
        moment(value).isBefore(
          moment(this.patientBenefitForm.get("effectiveDate").value)
        )
      ) {
        this.warningPopup(
          "Expired Date can not be prior to Effective Date.",
          "expiredDate",
          previousValue
        );
      }
    }
  }

  onSubscriberIdChanges(s: FormGroup) {
    s.get("subscriberId").updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
    this.changeImpactToClaim();
    if (s.get("subscriberId").valid) {
      this.duplicatedSubscriberId = this.checkDuplicatedSubscriberId(
        s.get("subscriberId").value
      );
    }
  }

  checkDuplicatedSubscriberId(value) {
    return (
      this.subscriberHistory.controls.filter(
        (s) => s.get("subscriberId").value == value
      ).length > 1
    );
  }

  setCorrectMedicaidNumberValue() {
    if (this.patientBenefitForm.get("medicaidNumber").value == null) {
      this.patientBenefitForm
        .get("medicaidNumber")
        .setValue("", { emitEvent: false });
    }
  }

  onPhoneNumberChanges() {
    if (
      this.patientBenefitForm.get("subscriber").get("phoneNumber").value &&
      this.patientBenefitForm.get("subscriber").get("phoneNumber").value
        .length > 0
    ) {
      this.patientBenefitForm
        .get("subscriber")
        .get("phoneNumber")
        .setValidators(
          Validators.compose([
            Validators.pattern(this.phoneNumberPattern),
            Validators.minLength(10),
            Validators.maxLength(10),
          ])
        );
    } else {
      this.patientBenefitForm
        .get("subscriber")
        .get("phoneNumber")
        .clearValidators();
    }
    this.patientBenefitForm
      .get("subscriber")
      .get("phoneNumber")
      .updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  onAdmitBenefitPeriodChanges(value) {
    if (value && +value === 0) {
      this.patientBenefitForm
        .get("hospiceEligibility")
        .get("admitBenefitPeriod")
        .setErrors({ invalidBenefitPeriod: true });
    } else if (
      this.patientBenefitForm.value.hospiceEligibility
        .admitBenefitPeriodStartDate !== null &&
      (value == null || value == 0)
    ) {
      this.patientBenefitForm
        .get("hospiceEligibility")
        .get("admitBenefitPeriod")
        .setErrors({ missingBenefitPeriod: true });
    } else {
      this.patientBenefitForm
        .get("hospiceEligibility")
        .get("admitBenefitPeriod")
        .setErrors(null);
    }

    this.changeImpactToClaim();
  }

  onAdmitBenefitPeriodStartDateChanges(value) {
    let benefitPeriod =
      this.patientBenefitForm.value.hospiceEligibility.admitBenefitPeriod;
    if (value != null) {
      if (benefitPeriod == null || benefitPeriod == 0) {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("admitBenefitPeriod")
          .setErrors({ missingBenefitPeriod: true });
      }
    } else {
      if (
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("admitBenefitPeriod").invalid &&
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("admitBenefitPeriod").errors["missingBenefitPeriod"] != null
      ) {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("admitBenefitPeriod")
          .setErrors(null);
      }
    }
    if (
      this.selectedPayerLevel &&
      this.selectedPayerLevel.value !== ROOMANDBOARD
    ) {
      this.showAlertWhenBenefitDataChanged();
    }
  }

  onMedicaidNumberChanges(value) {
    this.setMedicaidPendingDisabled(value);
  }

  setMedicaidPendingDisabled(value) {
    this.medicaidPendingDisabled = value.length != 0;
    if (this.medicaidPendingDisabled) {
      this.medicaidPending = false;
    }
  }

  clearMedicaidNumber() {
    this.patientBenefitForm
      .get("medicaidNumber")
      .setValue("", { emitEvent: false });
  }

  showAlertWhenBenefitDataChanged() {
    this.changeImpactToClaim();
    if (this.patientStatus === "PATIENT" && this.impactToClaim) {
      this.showWarningOfCompliance(
        `NOTICE: Changing benefit period information WILL impact existing certifications and Face to Face visits. Please make sure you review these records for the patient to ensure you remain in compliance.`
      );
    }
  }

  changeImpactToClaim() {
    if (this.patientBenefitForm.get("readyToBill").value === "Yes") {
      this.impactToClaim = true;
    }
  }

  warningPopup(message: string, field: string, value: string) {
    const alert = this.alertCtrl.create({
      enableBackdropDismiss: false,
      title: "Warning Message",
      message: message,
      buttons: [
        {
          text: "OK",
          handler: () => {
            this.patientBenefitForm
              .get(field)
              .setValue(value, { emitEvent: false });
          },
        },
      ],
    });

    alert.present();
  }

  get authorizations(): FormArray {
    return this.patientBenefitForm.get("authorizations") as FormArray;
  }

  get liabilities(): FormArray {
    return this.patientBenefitForm
      .get("roomAndBoardEligibility")
      .get("patientLiability") as FormArray;
  }

  get roomAndBoardEligibility(): AbstractControl {
    return this.patientBenefitForm.get(
      "roomAndBoardEligibility"
    ) as AbstractControl;
  }

  newAuthorization(): FormGroup {
    return this.fb.group({
      authorizationType: ["", Validators.required],
      description: [""],
      authorizationValue: [null],
      quantityAuthorized: [null],
      authorizationNumber: ["", Validators.required],
      authorizationStart: ["", Validators.required],
      authorizationEnd: [""],
      authorizedBy: [""],
      notes: [""],
    });
  }

  get subscriberHistory(): FormArray {
    return this.subscriberHistoryForm.get("subscriberHistory") as FormArray;
  }

  newSubscriberHistory(): FormGroup {
    return this.fb.group({
      subscriberId: ["", Validators.required],
      effectiveDate: ["", Validators.required],
      expiredDate: ["-"],
    });
  }

  get billingDetailsForm(): FormArray {
    return this.billingDetails.get("billingDetails") as FormArray;
  }

  newBillingDetails(): FormGroup {
    return this.fb.group({
      start: ["", Validators.required],
      end: ["-"],
      billRate: ["", Validators.required],
      careLevel: [0],
    });
  }

  addSubscriberHistory() {
    let s = this.newSubscriberHistory();
    if (
      this.patientBenefitForm.get("payerType").value === "MEDICARE" &&
      !this.isBVID
    ) {
      s.get("subscriberId").setValidators(
        Validators.compose([
          Validators.pattern(this.subscriberIdPattern),
          Validators.maxLength(11),
        ])
      );
      s.get("subscriberId").updateValueAndValidity({ emitEvent: false });
    } else {
      s.get("subscriberId").clearValidators();
      s.get("subscriberId").setErrors(null);
    }
    this.subscriptions.push(
      s
        .get("subscriberId")
        .valueChanges.subscribe(() => this.onSubscriberIdChanges(s))
    );
    this.subscriptions.push(
      s
        .get("effectiveDate")
        .valueChanges.subscribe(() => this.updateSubscriberIdDates())
    );
    this.subscriberHistory.insert(0, s);
    this.setupMedicareRealtimeSync();
    this.updateSubscriberIdDates();
  }

  removeSubscriberHistory(i) {
    const alert = this.alertCtrl.create({
      enableBackdropDismiss: false,
      title: "Warning",
      message:
        "Do you want to remove this Subscriber ID? This action can not be undone.",
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "OK",
          handler: () => {
            this.subscriberHistory.removeAt(i);
            this.setupMedicareRealtimeSync();
            this.updateSubscriberIdDates();
          },
        },
      ],
    });
    alert.present();
  }

  updateSubscriberIdDates() {
    if (this.subscriberHistory.controls.length > 1) {
      let minDate = moment(
        this.subscriberHistory.controls[1].get("effectiveDate").value
      )
        .add(1, "day")
        .toDate();
      this.subscriberIdMinDateValue = {
        year: minDate.getFullYear(),
        month: minDate.getMonth() + 1,
        day: minDate.getDate(),
      };
    }

    let nextSubscriberId: AbstractControl;
    this.subscriberHistory.controls.forEach((s) => {
      if (nextSubscriberId && nextSubscriberId.get("effectiveDate").value) {
        s.get("expiredDate").setValue(
          moment(nextSubscriberId.get("effectiveDate").value)
            .subtract(1, "day")
            .format("MM/DD/YYYY")
        );
      } else {
        s.get("expiredDate").setValue("-");
      }
      nextSubscriberId = s;
    });
    this.changeImpactToClaim();
  }

  addBillingEffectiveDate() {
    let s = this.newBillingDetails();
    this.subscriptions.push(
      s
        .get("start")
        .valueChanges.subscribe(() => this.updateBillingDetailsDates())
    );
    this.billingDetailsForm.insert(0, s);
    this.updateBillingDetailsDates();
  }

  updateBillingDetailsDates() {
    let nextSubscriberId: AbstractControl;
    this.billingDetailsForm.controls.forEach((d) => {
      if (
        nextSubscriberId &&
        nextSubscriberId.get("start").value &&
        nextSubscriberId.get("start").value != ""
      ) {
        d.get("end").setValue(
          moment(nextSubscriberId.get("start").value)
            .subtract(1, "day")
            .format("MM/DD/YYYY")
        );
      } else {
        d.get("end").setValue("-");
      }
      nextSubscriberId = d;
    });
  }

  formatBillingDetailsRates(form) {
    form.forEach((d) => {
      if (d.start != "" && !this.dateRegex.test(d.start)) {
        d.start = moment(d.start).format(this.dateFormat);
      }
      if (d.end && d.end == "-") {
        d.end = "";
      } else if (d.end) {
        d.end = moment(d.end).format(this.dateFormat);
      }

      d.careLevel = parseInt(d.careLevel);
    });
  }

  removeBillingDetail(i) {
    const alert = this.alertCtrl.create({
      enableBackdropDismiss: false,
      title: "Warning",
      message:
        "Do you want to remove this billing detail? This action can not be undone.",
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "OK",
          handler: () => {
            this.billingDetailsForm.removeAt(i);
            this.updateBillingDetailsDates();
          },
        },
      ],
    });
    alert.present();
  }

  newLiability(): FormGroup {
    return this.fb.group({
      patientLiability: false,
      liabilityAmount: 0,
      fromDate: "",
      toDate: "",
    });
  }

  addAuthorization() {
    this.authorizations.push(this.newAuthorization());
  }

  addLiability() {
    let admitBenefitPeriod =
      this.roomAndBoardEligibility.value.admitBenefitPeriod;
    let admitBenefitPeriodStartDate =
      this.roomAndBoardEligibility.value.admitBenefitPeriodStartDate;
    this.liabilities.push(this.newLiability());
    this.roomAndBoardEligibility.value.admitBenefitPeriod = admitBenefitPeriod;
    this.roomAndBoardEligibility.value.admitBenefitPeriodStartDate =
      admitBenefitPeriodStartDate;
  }

  removeAuthorization(i) {
    const alert = this.alertCtrl.create({
      enableBackdropDismiss: false,
      title: "Warning",
      message:
        "Do you want to remove this authorization? This action can not be undone.",
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "OK",
          handler: () => {
            this.authorizations.removeAt(i);
          },
        },
      ],
    });
    alert.present();
  }

  removeLiability(i) {
    const alert = this.alertCtrl.create({
      enableBackdropDismiss: false,
      title: "Warning",
      message:
        "Do you want to remove this liability? This action can not be undone.",
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "OK",
          handler: () => {
            this.liabilities.removeAt(i);
          },
        },
      ],
    });
    alert.present();
  }

  mapCurrentPatientBenefitToForm() {
    if (this.currentPatientBenefit.authorizations) {
      this.currentPatientBenefit.authorizations.forEach(() => {
        this.addAuthorization();
      });
    }

    if (!this.currentPatientBenefit.roomAndBoardEligibility) {
      this.currentPatientBenefit.roomAndBoardEligibility = {
        admitBenefitPeriod: null,
        admitBenefitPeriodStartDate: "",
        patientLiability: [],
      };
    }

    if (this.currentPatientBenefit.roomAndBoardEligibility.patientLiability) {
      this.currentPatientBenefit.roomAndBoardEligibility.patientLiability.forEach(
        () => {
          this.addLiability();
        }
      );
      if (this.liabilities.controls.length > 1) {
        this.liabilities.removeAt(0);
      }
    }

    if (
      !this.currentPatientBenefit.subscriber.subscriberHistory ||
      this.currentPatientBenefit.subscriber.subscriberHistory.length == 0
    ) {
      this.currentPatientBenefit.subscriber.subscriberHistory = [];
      let initialHistory: SubscriberHistory = {
        subscriberId: this.currentPatientBenefit.subscriber.subscriberId,
        effectiveDate: this.currentPatientBenefit.effectiveDate,
        expiredDate: "-",
      };
      this.currentPatientBenefit.subscriber.subscriberHistory.push(
        initialHistory
      );
    }
    this.subscriberHistory.controls = [];
    if (this.currentPatientBenefit.subscriber.subscriberHistory) {
      this.currentPatientBenefit.subscriber.subscriberHistory.sort(
        (a, b) => Date.parse(b.effectiveDate) - Date.parse(a.effectiveDate)
      );
      this.currentPatientBenefit.subscriber.subscriberHistory.forEach((s) => {
        if (s.expiredDate == "" || s.expiredDate == "-") {
          s.expiredDate = "-";
        } else {
          s.expiredDate = moment(s.expiredDate).format("MM/DD/YYYY");
        }
        this.addSubscriberHistory();
      });
    }
    try {
      this.patientBenefitForm.patchValue(this.currentPatientBenefit, {
        emitEvent: false,
      });
      this.patientBenefitForm
        .get("benefitType")
        .setValue(this.careType, { emitEvent: false });
    } catch (error) {
      console.error(error);
    }
    this.subscriberHistoryForm.patchValue(
      this.currentPatientBenefit.subscriber,
      { emitEvent: false }
    );

    this.loadMonthlyEligibility();

    this.billingDetailsForm.controls = [];
    if (
      this.currentPatientBenefit &&
      this.currentPatientBenefit.billingDetails
    ) {
      this.currentPatientBenefit.billingDetails.sort(
        (a, b) => Date.parse(a.start) - Date.parse(b.start)
      );
      this.currentPatientBenefit.billingDetails.forEach((s) => {
        if (s.end != "") {
          s.end = moment(s.end).format("MM/DD/YYYY");
        } else {
          s.end = "-";
        }
        let newBillingDetail = this.newBillingDetails();
        newBillingDetail.get("start").setValue(s.start);
        newBillingDetail.get("end").setValue(s.end);
        newBillingDetail.get("billRate").setValue(s.billRate);
        newBillingDetail.get("careLevel").setValue(s.careLevel.toString());
        this.billingDetailsForm.insert(0, newBillingDetail);
      });
      try {
        this.billingDetails.patchValue(
          this.currentPatientBenefit.billingDetails,
          { emitEvent: false }
        );
      } catch (error) {
        console.error(error);
      }
    } else if (
      this.currentPatientBenefit &&
      this.currentPatientBenefit.payerPrimacyLevel == "ROOMANDBOARD" &&
      this.currentPatientBenefit.billingDetails == null
    ) {
      let newBillingDetail = this.newBillingDetails();
      newBillingDetail
        .get("start")
        .setValue(this.currentPatientBenefit.effectiveDate);
      newBillingDetail
        .get("end")
        .setValue(this.currentPatientBenefit.expiredDate);
      newBillingDetail.get("billRate").setValue("FACILITY");
      newBillingDetail.get("careLevel").setValue("1");
      this.billingDetailsForm.insert(0, newBillingDetail);
    } else if (this.currentPatientBenefit.payerPrimacyLevel != "ROOMANDBOARD") {
      this.patientBenefitForm.removeControl("billingDetails");
    }

    if (this.patientBenefitForm.get("medicaidNumber").value == null) {
      this.patientBenefitForm
        .get("medicaidNumber")
        .setValue("", { emitEvent: false });
    }

    this.patientEligibilityVerified =
      this.currentPatientBenefit.eligibilityVerified;

    if (this.currentPatientBenefit.readyToBill === "Hold") {
      this.patientBenefitForm
        .get("reasonForHold")
        .setValidators([Validators.required]);
      this.patientBenefitForm.get("reasonForHold").updateValueAndValidity();
    }

    this.setPlanList(this.currentPatientBenefit.payerId);
    this.selectedRelationship = this.relationshipList.find(
      (r) => r.value === this.currentPatientBenefit.subscriber.relationship
    );
    this.selectedState = this.stateOptions.find(
      (f) => f.value === this.currentPatientBenefit.subscriber.state
    );
    this.selectedPlanState = this.stateOptions.find(
      (f) => f.value === this.currentPatientBenefit.plan.state
    );
    this.selectedPayerLevel = this.payerLevelList.find(
      (p) => p.value === this.currentPatientBenefit.payerPrimacyLevel
    );
    if (this.selectedPayerLevel.value == "ROOMANDBOARD") {
      this.payerTypeList = this.payerTypeList.filter(
        (x) => x.value == "MEDICAID" || x.value == "COMMERCIAL"
      );
    }
    this.selectedPayerType = this.payerTypeList.find(
      (f) => f.value === this.currentPatientBenefit.payerType
    );
    this.payerNameOptions = this.payerNameList
      .filter((p) => this.isPayerNeeded(p))
      .map((m) => {
        return {
          name: m.payerName,
          value: m.payerId,
          disabled: false,
        };
      });

    this.selectedPayerName = this.payerNameOptions.find(
      (f) => f.value === this.currentPatientBenefit.payerId
    );

    this.updateIsVBID();
    // sets latest subscriber ID to medicareNumber
    if (this.patientBenefitForm.get("payerType").value === "MEDICARE") {
      if (this.subscriberHistory.controls.length > 0) {
        const latestSubscriberId =
          this.subscriberHistory.controls[0].get("subscriberId").value;
        this.patientBenefitForm
          .get("medicareNumber")
          .setValue(latestSubscriberId);
      }
      this.patientBenefitForm.get("medicareNumber").disable();
    } else {
      this.patientBenefitForm.get("medicareNumber").enable();
    }
    this.setupMedicareRealtimeSync();
  }

  isPayerNeeded(payerConfig) {
    if (
      this.selectedPayerLevel &&
      this.selectedPayerLevel.value == ROOMANDBOARD
    ) {
      return (
        payerConfig.payerType === this.selectedPayerType.value &&
        payerConfig.benefitType.includes(ROOMANDBOARD)
      );
    }
    return payerConfig.payerType === this.selectedPayerType.value;
  }

  payerTypeChanged(val: Config) {
    this.selectedPayerType = val;

    this.payerNameOptions = this.payerNameList
      .filter((p) => this.isPayerNeeded(p))
      .map((m) => {
        return {
          name: m.payerName,
          value: m.payerId,
          disabled: false,
        };
      });

    this.resetPlan();
  }

  checkPartialEditMode(benefit: PatientBenefits) {
    if (benefit.expiredDate !== "" && benefit.expiredDate !== null) {
      if (benefit.payerPrimacyLevel === PRIMARY) {
        this.partialEdit = true;
      } else if (
        this.mostRecentSecondaryBenefit &&
        benefit.payerPrimacyLevel === SECONDARY &&
        benefit.benefitsId !== this.mostRecentSecondaryBenefit.benefitsId
      ) {
        this.partialEdit = true;
      }
    }
  }

  showSearchDropdownPopup(config: Config[], selectedItem: Config) {
    const options = config.map((m) => {
      if (selectedItem) {
        m.disabled = m.value === selectedItem.value;
      }
      return {
        name: m.name,
        value: m,
        disabled: false,
      };
    });

    const searchable = true;
    const searchPlaceholder = "";
    const emptyMessage = "Not found.";

    return this.popover.create(
      "CareTeamsSelectPage",
      { options, searchable, searchPlaceholder, emptyMessage },
      { cssClass: "care-teams-select-popover" }
    );
  }

  showPayerNameOptions(ev) {
    let popover = this.showSearchDropdownPopup(
      this.payerNameOptions,
      this.selectedPayerName
    );

    popover.onDidDismiss((data) => {
      if (data) {
        this.selectedPayerName = this.payerNameOptions.find(
          (s) => s.value == data.value
        );
        this.patientBenefitForm.controls["payerId"].setValue(
          this.selectedPayerName.value
        );
        this.patientBenefitForm.controls["payerName"].setValue(
          this.selectedPayerName.name
        );
        this.clearMedicaidNumber();
        this.setMedicaidPendingDisabled(
          this.patientBenefitForm.get("medicaidNumber").value
        );
        this.resetPlan();
        this.setPlanList(data.value);
        this.changeImpactToClaim();
      }
    });

    popover.present({ ev });
  }

  setPlanList(payerId: string) {
    let payer = this.payerNameList.find((p) => p.payerId === payerId);
    if (payer) {
      this.planList = payer.plans;
    } else {
      this.planList = [];
    }
  }

  resetPlan() {
    this.patientBenefitForm.get("plan").reset();
    this.selectedPlanState = null;
    this.updateAddNewPlan(false);
  }

  planNameChanged(plan: Plan) {
    if (!plan.newPlanName) {
      plan.newPlanName = "";
    }

    plan.planName = plan.planName === "-" ? "" : plan.planName;

    if (plan && plan.value && plan.value === "Add New") {
      this.updateAddNewPlan(true);
    } else {
      this.updateAddNewPlan(false);
    }
    this.selectedPlanState = this.stateOptions.find(
      (f) => f.value === plan.state
    );
    this.patientBenefitForm.get("plan").setValue(plan);
  }

  updateAddNewPlan(show: boolean) {
    this.addNewPlan = show;
  }

  validateNewPlanName() {
    let newPlanName = this.patientBenefitForm
      .get("plan")
      .get("newPlanName")
      .value.trim();

    this.planList.forEach((plan) => {
      if (plan.planName.toUpperCase() === newPlanName.toUpperCase()) {
        this.patientBenefitForm.get("plan").get("newPlanName").reset();
        this.onFailure(
          newPlanName +
            " already exists. Please use a different name or select " +
            newPlanName +
            " from the list."
        );
      }
    });
  }

  isValidPlanAddress(): boolean {
    let plan: Plan = this.patientBenefitForm.get("plan").value;
    let isValid = true;

    if (
      this.addNewPlan &&
      (plan.planName === "" ||
        plan.address1 === "" ||
        plan.city === "" ||
        plan.state === "" ||
        plan.zipCode === "")
    ) {
      this.patientBenefitForm.get("plan").get("planName").setValue("Add New");
      this.patientBenefitForm.get("plan").get("value").setValue("Add New");
      this.onFailure("Please enter a valid Plan Adddress.");
      isValid = false;
    }

    return isValid;
  }

  selectPlanState(ev) {
    let popover = this.showSearchDropdownPopup(
      this.stateOptions,
      this.selectedPlanState
    );

    popover.onDidDismiss((data) => {
      if (data) {
        this.selectedPlanState = this.stateOptions.filter(
          (s) => s.value == data.value
        )[0];
        this.patientBenefitForm
          .get("plan")
          .get("state")
          .setValue(this.selectedPlanState.value);
      }
    });

    popover.present({ ev });
  }

  selectState(ev) {
    let popover = this.showSearchDropdownPopup(
      this.stateOptions,
      this.selectedState
    );

    popover.onDidDismiss((data) => {
      if (data) {
        this.selectedState = this.stateOptions.filter(
          (s) => s.value == data.value
        )[0];
        this.patientBenefitForm
          .get("subscriber")
          .get("state")
          .setValue(this.selectedState.value);
      }
    });

    popover.present({ ev });
  }

  relationshipChanged(val: Config) {
    if (
      this.selectedRelationship &&
      this.selectedRelationship.value === "SELF" &&
      val.value !== "SELF"
    ) {
      this.patientBenefitForm.get("subscriber").reset();
      this.patientBenefitForm
        .get("subscriber")
        .get("relationship")
        .setValue(val.value);
      this.selectedState = null;
    }
    this.selectedRelationship = val;
    if (val.value === "SELF") {
      this.getPatientChart(true);
    }
  }

  getPatientChart(shouldMapSelfRelationship) {
    if (!this.patientChart) {
      this.showLoadingMessage();
      this.patientChartService.getPatientById(this.patientId).subscribe(
        (response) => {
          this.patientChart = response ? response : null;

          this.mapSelfRelationship(shouldMapSelfRelationship);
          this.utilService.decreaseSpinnerCount();
        },
        (_err) => {
          this.decreaseSpinnerAndShowFailure(
            "Failed to retrieve patient chart information, please retry."
          );
        }
      );
    } else {
      this.mapSelfRelationship(shouldMapSelfRelationship);
    }
  }

  mapSelfRelationship(shouldMapSelfRelationship) {
    if (
      shouldMapSelfRelationship &&
      this.patientChart &&
      this.patientChart.patient
    ) {
      if (
        this.patientBenefitActionEvent &&
        this.patientBenefitActionEvent.patientBenefit &&
        this.patientBenefitActionEvent.patientBenefit.subscriber
      ) {
        // this.patientBenefitForm.get('subscriber').get('subscriberId')
        //     .setValue(this.patientBenefitActionEvent.patientBenefit.subscriber.subscriberId, { emitEvent: false });

        this.patientBenefitForm
          .get("subscriber")
          .get("groupNumber")
          .setValue(
            this.patientBenefitActionEvent.patientBenefit.subscriber.groupNumber
          );
      }

      this.patientBenefitForm
        .get("subscriber")
        .get("relationship")
        .setValue("SELF", { emitEvent: false });
      this.patientBenefitForm
        .get("subscriber")
        .get("firstName")
        .setValue(this.patientChart.patient.firstName);
      this.patientBenefitForm
        .get("subscriber")
        .get("lastName")
        .setValue(this.patientChart.patient.lastName);
      this.patientBenefitForm
        .get("subscriber")
        .get("middleInitial")
        .setValue(this.patientChart.patient.middleInitial);
      this.patientBenefitForm
        .get("subscriber")
        .get("dateOfBirth")
        .setValue(this.patientChart.patient.birthDate);
      this.patientBenefitForm
        .get("subscriber")
        .get("emailAddress")
        .setValue(this.patientChart.patient.emailAddress);

      if (this.patientChart.patient.homeAddress) {
        this.selectedState = this.stateOptions.find(
          (s) => s.value === this.patientChart.patient.homeAddress.state
        );
        this.patientBenefitForm
          .get("subscriber")
          .get("state")
          .setValue(this.selectedState.value);
        this.patientBenefitForm
          .get("subscriber")
          .get("address1")
          .setValue(this.patientChart.patient.homeAddress.address1);
        this.patientBenefitForm
          .get("subscriber")
          .get("city")
          .setValue(this.patientChart.patient.homeAddress.city);
        this.patientBenefitForm
          .get("subscriber")
          .get("zipCode")
          .setValue(this.patientChart.patient.homeAddress.zip);
        this.patientChart.patient.homeAddress.zipExtension
          ? this.patientBenefitForm
              .get("subscriber")
              .get("zipCodeExtension")
              .setValue("")
          : this.patientBenefitForm
              .get("subscriber")
              .get("zipCodeExtension")
              .setValue(this.patientChart.patient.homeAddress.zipExtension);
      }

      if (
        this.patientChart.patient.phoneNumber &&
        this.patientChart.patient.phoneNumber.number
      ) {
        this.patientBenefitForm
          .get("subscriber")
          .get("phoneNumber")
          .setValue(
            this.formatPhoneNumber(this.patientChart.patient.phoneNumber.number)
          );
      }
    }
  }

  formatPhoneNumber(phoneNumber: string) {
    return phoneNumber.replace(/[^0-9]/g, "").substring(0, 10);
  }

  formatDate(date) {
    if (date !== null && date != "") {
      return moment(date).format("YYYY-MM-DD");
    } else {
      return "";
    }
  }

  mapActivePrimaryBenefitDetails() {
    if (
      !this.partialEdit &&
      this.mostRecentPrimaryBenefit &&
      this.patientBenefitForm.get("benefitsId").value !==
        this.mostRecentPrimaryBenefit.benefitsId &&
      this.patientBenefitForm.get("payerPrimacyLevel").value === PRIMARY
    ) {
      this.patientBenefitForm.controls["activePrimaryBenefitsId"].setValue(
        this.mostRecentPrimaryBenefit.benefitsId
      );
      this.patientBenefitForm.controls[
        "expiredActivePrimaryBenefitDate"
      ].setValue(
        moment(this.patientBenefitForm.get("effectiveDate").value)
          .subtract(1, "day")
          .format("YYYY-MM-DD")
      );
    } else if (
      !this.partialEdit &&
      !this.editMode &&
      this.mostRecentSecondaryBenefit &&
      this.patientBenefitForm.get("payerPrimacyLevel").value === SECONDARY &&
      this.mostRecentSecondaryBenefit.status === "ACTIVE" &&
      (this.mostRecentSecondaryBenefit.expiredDate === "" ||
        this.mostRecentSecondaryBenefit.expiredDate === "-")
    ) {
      this.patientBenefitForm.controls["activePrimaryBenefitsId"].setValue(
        this.mostRecentSecondaryBenefit.benefitsId
      );
      this.patientBenefitForm.controls[
        "expiredActivePrimaryBenefitDate"
      ].setValue(
        moment(this.patientBenefitForm.get("effectiveDate").value)
          .subtract(1, "day")
          .format("YYYY-MM-DD")
      );
    } else {
      this.patientBenefitForm.controls["activePrimaryBenefitsId"].setValue(
        null
      );
      this.patientBenefitForm.controls[
        "expiredActivePrimaryBenefitDate"
      ].setValue(null);
    }
  }

  showWarningBeforeSavingBenefit(message: string) {
    let alert = this.alertCtrl.create({
      title: "Warning Message",
      message: message,
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "Proceed",
          handler: () => {
            this.saveBenefit();
          },
        },
      ],
    });
    alert.present();
  }

  showWarningOfCompliance(message: string) {
    let alert = this.alertCtrl.create({
      title: "Warning Message",
      message: message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  }

  isExpiryInvalid() {
    this.mapActivePrimaryBenefitDetails();
    if (
      this.patientBenefitForm.get("activePrimaryBenefitsId").value !== null &&
      this.patientBenefitForm.get("expiredActivePrimaryBenefitDate").value !==
        null
    ) {
      let payerType = this.patientBenefitForm.get("payerPrimacyLevel").value;
      let payerName =
        payerType === PRIMARY
          ? this.mostRecentPrimaryBenefit.payerName
          : this.mostRecentSecondaryBenefit.payerName;

      if (this.validateAdmitDate()) {
        return `*Please note: you cannot add this benefit as it will expire ${payerName} as the ${
          payerType.charAt(0) + payerType.slice(1).toLowerCase()
        } Payer before the patient’s admit date. Please check the Payer Effective Date selected for this new benefit or update the current benefit information.`;
      } else if (
        this.navBarService.getAdmitDate() == null ||
        this.navBarService.getAdmitDate() == "-"
      ) {
        return `*Please note: you cannot add this benefit as it will expire ${payerName} as the ${
          payerType.charAt(0) + payerType.slice(1).toLowerCase()
        } Payer. Please check your selected Payer Level.`;
      }
    }
  }

  isRNBExpireDateValid() {
    this.disableExpiredDate = false;
    if (!this.selectedPayerLevel) return;
    if (this.editMode) return;
    const rnbActiveBenefit = this.patientBenefitsList.find(
      (b) =>
        b.payerPrimacyLevel === this.selectedPayerLevel.value &&
        b.status === "ACTIVE"
    );
    if (rnbActiveBenefit && this.selectedPayerLevel.value === "ROOMANDBOARD") {
      this.mapActivePrimaryBenefitDetails();
      let payerType = this.payerLevelList.find(
        (p) => p.value === rnbActiveBenefit.payerPrimacyLevel
      ).name;
      let payerName = rnbActiveBenefit.payerName;
      let expiredDate = null;
      let admitDate = moment(this.patientChart.admittedDatetime).format(
        "MM/DD/YYYY"
      );
      if (
        this.patientBenefitForm.get("effectiveDate") &&
        this.patientBenefitForm.get("effectiveDate").value
      )
        expiredDate = moment(
          this.patientBenefitForm.get("effectiveDate").value
        ).format("MM/DD/YYYY");
      if (
        this.patientChart.patientStatus.toLowerCase() === "on-service" &&
        expiredDate != null &&
        new Date(expiredDate) <= new Date(admitDate)
      ) {
        return `*Please note: you cannot add this benefit, as it will expire ${payerName} as the ${
          payerType.charAt(0) + payerType.slice(1).toLowerCase()
        } Payer before the patient’s admit date. Please check the Payer Effective Date selected for this new benefit or update the current benefit information.`;
      } else if (
        this.patientChart.patientStatus.toLowerCase() !== "on-service"
      ) {
        return `*Please note: you cannot add this benefit, as it will expire ${payerName} as the ${
          payerType.charAt(0) + payerType.slice(1).toLowerCase()
        } Payer. Please check your selected Payer Level.`;
      }
    } else if (
      this.selectedPayerLevel.value === "ROOMANDBOARD" &&
      this.patientChart.patientStatus.toLowerCase() === "on-service"
    ) {
      this.disableExpiredDate = true;
    }
  }

  validateAdmitDate() {
    return (
      this.navBarService.getAdmitDate() != null &&
      this.navBarService.getAdmitDate() !== "-" &&
      Date.parse(
        this.patientBenefitForm.get("expiredActivePrimaryBenefitDate").value
      ) < Date.parse(this.navBarService.getAdmitDate())
    );
  }

  validateBillingDetails() {
    if (this.selectedPayerLevel.value === "ROOMANDBOARD") {
      for (let i = 0; i < this.billingDetailsForm.controls.length; i++) {
        let detail = this.billingDetailsForm.controls[i].value;
        if (detail.billRate == "") return true;
        else if (
          detail.billRate == "FACILITY" &&
          (detail.careLevel == "" || detail.careLevel == 0)
        ) {
          return true;
        }
      }
    }
    return false;
  }

  minExpirationDate() {
    if (
      this.navBarService.getAdmitDate() != null &&
      this.navBarService.getAdmitDate() !== "-"
    ) {
      let admitDate = moment(this.navBarService.getAdmitDate());
      return {
        year: admitDate.year(),
        month: admitDate.month() + 1,
        day: admitDate.date(),
      };
    }

    return false;
  }

  minBillingEffectiveDate() {
    if (
      !this.patientBenefitForm.get("effectiveDate") ||
      this.patientBenefitForm.get("effectiveDate").value == ""
    )
      return false;
    let minDate: moment.Moment = moment(
      this.patientBenefitForm.get("effectiveDate").value
    );
    if (this.billingDetailsForm.controls.length > 1) {
      for (let i = 0; i < this.billingDetailsForm.controls.length; i++) {
        let detail = this.billingDetails.value.billingDetails[i];
        if (!detail.start || detail.start == "") continue;
        if (detail.start == this.patientBenefitForm.get("effectiveDate").value)
          continue;
        let start = moment(detail.start);
        if (start > minDate) minDate = start;
      }
      minDate.add(1, "days");
    }
    return {
      year: minDate.year(),
      month: minDate.month() + 1,
      day: minDate.date(),
    };
  }

  calculateBillExpirationDate(index) {
    let modified = this.billingDetails.controls.billingDetails.value;
    let date = this.billingDetails.controls.billingDetails.value[index].start;
    if (date && date != "") {
      modified[index].start = moment(date).format(this.dateFormat);
    } else return;

    if (this.billingDetailsForm.controls.length > 1) {
      let newDetail = this.billingDetails.value.billingDetails[0];
      if (newDetail.start != "") {
        let newEffectiveDate = moment(newDetail.start).subtract(1, "days");
        modified[1].end = newEffectiveDate.format(this.dateFormat);
      }
    }
    this.billingDetails.controls.billingDetails.setValue(modified);
  }

  submitForm() {
    if (this.selectedPayerLevel.value !== "ROOMANDBOARD") {
      this.patientBenefitForm.removeControl("billingDetails");
    }
    this.mapActivePrimaryBenefitDetails();
    this.sanitizePatientBenefitsForm();

    if (!this.isValidPlanAddress()) {
      return false;
    }

    if (this.isPayerOverlap()) {
      return false;
    }

    if (this.impactToClaim) {
      this.showWarningBeforeSavingBenefit(
        "NOTE!! This change could impact claims already submitted for this patient."
      );
    } else {
      if (
        this.patientBenefitForm.get("payerPrimacyLevel").value === PRIMARY &&
        this.patientBenefitForm.get("activePrimaryBenefitsId").value !== null &&
        this.patientBenefitForm.get("expiredActivePrimaryBenefitDate").value !==
          null
      ) {
        let expiredDate = moment(
          this.patientBenefitForm.get("expiredActivePrimaryBenefitDate").value
        ).format("MM/DD/YYYY");
        let message = `Adding this benefit will expire ${this.mostRecentPrimaryBenefit.payerName} as a primary benefit on ${expiredDate}.`;
        this.showWarningBeforeSavingBenefit(message);
      } else if (
        this.patientBenefitForm.get("payerPrimacyLevel").value === SECONDARY &&
        this.patientBenefitForm.get("activePrimaryBenefitsId").value !== null &&
        this.patientBenefitForm.get("expiredActivePrimaryBenefitDate").value !==
          null
      ) {
        let expiredDate = moment(
          this.patientBenefitForm.get("expiredActivePrimaryBenefitDate").value
        ).format("MM/DD/YYYY");
        let message = `Adding this benefit will expire ${this.mostRecentSecondaryBenefit.payerName} as a secondary benefit on ${expiredDate}.`;
        this.showWarningBeforeSavingBenefit(message);
      } else {
        this.saveBenefit();
      }
    }
  }

  loadMonthlyEligibility() {
    const monthly =
      (this.currentPatientBenefit &&
        (this.currentPatientBenefit as any).monthlyEligibility) ||
      [];
    const current = monthly.find(
      (m) => m.month === this.currentEligibilityMonth
    );
    this.patientBenefitForm
      .get("aidCode")
      .setValue(current ? current.stateEligibilityCategoryCode : "", {
        emitEvent: false,
      });
    this.patientBenefitForm
      .get("shareOfCostMonthly")
      .setValue(
        current && current.shareOfCostMonthly !== null
          ? PatientBenefitsFormComponent.centsToDollars(
              current.shareOfCostMonthly
            )
          : null,
        { emitEvent: false }
      );
    this.monthlyEligibilityHistory = monthly
      .filter((m) => m.month !== this.currentEligibilityMonth)
      .sort((a, b) => (a.month < b.month ? 1 : -1));
  }

  saveBenefit() {
    const errorMsg = "Failed to create the benefit.";
    const successMsg = this.editMode
      ? "Benefit updated successfully."
      : "Benefit created successfully.";

    this.utilService.increaseSpinnerCount("Saving Benefit...");
    // Enable the field so its value is included
    this.patientBenefitForm.get("medicareNumber").enable({ emitEvent: false });
    const medicareControl = this.patientBenefitForm.get("medicareNumber");
    medicareControl.setValue((medicareControl.value || "").toUpperCase(), {
      emitEvent: false,
    });

    if (this.patientBenefitForm.get("payerType").value === "MEDICARE") {
      const latestSubscriberId =
        this.subscriberHistory.controls[0]?.get("subscriberId").value || "";
      this.patientBenefitForm
        .get("medicareNumber")
        .setValue(latestSubscriberId, { emitEvent: false });
      this.patientBenefitForm
        .get("medicareNumber")
        .enable({ emitEvent: false });
    }

    const formData = this.patientBenefitForm.value;
    formData.benefitType = this.careType;

    // CO-2315 item #11 / D-4: carry the biller's reason for an election-field edit.
    //
    // OPTIONAL. F1.S1 ships as written - "because she entered none" - and making it
    // required would change an existing save path for every biller on this form.
    //
    // It rides on reasonForPayerChange because that is the field BOTH server-side audits
    // read (AppendPayerChangeAudit and AppendElectionFieldAudit,
    // patient_benefits_model.go). Consequence, deliberately pinned by a spec rather than
    // discovered later: a save that changes the payer AND an election date stamps the same
    // string on both entries. Splitting the two is tracked separately - doing it here would
    // change the CO-3394 payer audit from inside a Medi-Cal ticket.
    //
    // Only sent when a field actually changed, so a plain save cannot relabel an unrelated
    // payer change with a stale election reason.
    this.applyElectionChangeReason(formData);

    // CO-3396: current-month Aid Code / SOC ride as a one-entry monthly
    // upsert; the backend merges by month and audits the change. The raw
    // form controls never go over the wire.
    if (this.showMonthlyEligibility) {
      const aidCode = this.patientBenefitForm.get("aidCode").value;
      const soc = this.patientBenefitForm.get("shareOfCostMonthly").value;
      if (aidCode || (soc !== null && soc !== "")) {
        const entry: any = {
          month: this.currentEligibilityMonth,
          stateEligibilityCategoryCode: aidCode || "",
          // CO-3618: the backend stores Share of Cost as integer cents
          // (money.Money); convert the typed dollar amount here so a whole
          // number ($300 / $55.00) is not saved 100x under.
          shareOfCostMonthly: PatientBenefitsFormComponent.dollarsToCents(soc),
        };
        // CO-3398 (A.4.1): a positive SOC carries its state-specific code
        // identifiers, resolved from the payer's state.
        if (entry.shareOfCostMonthly > 0) {
          const payer = (this.payerNameList || []).find(
            (p) => p.payerId === formData.payerId
          );
          const codes =
            payer && payer.state
              ? PatientBenefitsFormComponent.STATE_SOC_CODES[
                  payer.state.toUpperCase()
                ]
              : null;
          if (codes) {
            entry.stateSpecificCodes = codes;
          }
        }
        formData.monthlyEligibility = [entry];
      }
    }
    delete formData.aidCode;
    delete formData.shareOfCostMonthly;

    formData.subscriber.subscriberHistory =
      this.subscriberHistoryForm.value.subscriberHistory;
    if (this.selectedPayerLevel.value === "ROOMANDBOARD") {
      formData.billingDetails = this.billingDetailsForm.value;
      this.formatBillingDetailsRates(formData.billingDetails);
    }
    this.success = true;

    this.patientBenefitsService
      .postPatientBenefit(this.patientChartId, formData)
      .subscribe(
        (response) => {
          if (response) {
            this.utilService.decreaseSpinnerCount();
            const alert = this.alertCtrl.create({
              enableBackdropDismiss: false,
              title: "Success Message",
              // CO-2315 item #5 (F9.S6): the confirmation, plus any unrecognized
              // Aid Code warning the save returned.
              message: this.saveMessageFor(successMsg, response),
              buttons: [
                {
                  text: "OK",
                  handler: () => {
                    this.clearForm();
                  },
                },
              ],
            });
            alert.present();
          } else {
            this.success = false;
            this.utilService.decreaseSpinnerCount();
            this.onFailure(errorMsg);
          }
        },
        (_err) => {
          this.success = false;
          this.utilService.decreaseSpinnerCount();
          this.onFailure(errorMsg);
        }
      );
  }

  sanitizePatientBenefitsForm() {
    let oldImpactToClaim = this.impactToClaim;
    this.patientBenefitForm.controls["payerId"].setValue(
      this.selectedPayerName.value
    );
    this.patientBenefitForm.controls["payerName"].setValue(
      this.selectedPayerName.name
    );

    this.patientBenefitForm.get("addNewPayerPlan").setValue(this.addNewPlan);

    if (this.addNewPlan) {
      this.patientBenefitForm
        .get("plan")
        .get("planName")
        .setValue(this.patientBenefitForm.get("plan").get("newPlanName").value);
    }

    this.patientBenefitForm
      .get("expiredDate")
      .setValue(
        this.formatDate(this.patientBenefitForm.get("expiredDate").value),
        { emitEvent: false }
      );

    this.patientBenefitForm
      .get("effectiveDate")
      .setValue(
        this.formatDate(this.patientBenefitForm.get("effectiveDate").value),
        { emitEvent: false }
      );

    this.subscriberHistory.controls.forEach((s) => {
      s.get("subscriberId").setValue(
        String(s.get("subscriberId").value).toUpperCase(),
        { emitEvent: false }
      );
      if (s.value.effectiveDate !== "") {
        s.value.effectiveDate = this.formatDate(s.value.effectiveDate);
      }
      if (s.value.expiredDate !== "" && s.value.expiredDate !== "-") {
        s.value.expiredDate = this.formatDate(s.value.expiredDate);
      } else {
        s.value.expiredDate = "";
      }
    });
    this.patientBenefitForm
      .get("subscriber")
      .get("subscriberId")
      .setValue(this.subscriberHistory.controls[0].get("subscriberId").value);
    this.patientBenefitForm
      .get("subscriber")
      .get("subscriberIdChange")
      .setValue(this.subscriberHistory.dirty && !this.patientBenefitForm.dirty);

    this.patientBenefitForm
      .get("subscriber")
      .get("isVBID")
      .setValue(this.isBVID, { emitEvent: false });

    this.patientBenefitForm
      .get("medicaidPending")
      .setValue(this.medicaidPending, { emitEvent: false });

    this.patientBenefitForm
      .get("medicaidNumber")
      .setValue(String(this.patientBenefitForm.get("medicaidNumber").value), {
        emitEvent: false,
      });

    this.setCorrectMedicaidNumberValue();

    if (
      this.patientBenefitForm.get("subscriber").get("dateOfBirth").value !== ""
    ) {
      this.patientBenefitForm
        .get("subscriber")
        .get("dateOfBirth")
        .setValue(
          this.formatDate(
            this.patientBenefitForm.get("subscriber").get("dateOfBirth").value
          )
        );
    }

    if (
      this.patientBenefitForm.get("financial").get("coPay").value !== null &&
      this.patientBenefitForm.get("financial").get("coPay").value !== ""
    ) {
      this.patientBenefitForm
        .get("financial")
        .get("coPay")
        .setValue(
          Number(this.patientBenefitForm.get("financial").get("coPay").value)
        );
    } else {
      this.patientBenefitForm.get("financial").get("coPay").setValue(null);
    }

    if (
      this.patientBenefitForm.get("financial").get("limitAmount").value !==
        null &&
      this.patientBenefitForm.get("financial").get("limitAmount").value !== ""
    ) {
      this.patientBenefitForm
        .get("financial")
        .get("limitAmount")
        .setValue(
          Number(
            this.patientBenefitForm.get("financial").get("limitAmount").value
          )
        );
    } else {
      this.patientBenefitForm
        .get("financial")
        .get("limitAmount")
        .setValue(null);
    }

    if (
      this.patientBenefitForm.get("financial").get("deductible").value !==
        null &&
      this.patientBenefitForm.get("financial").get("deductible").value !== ""
    ) {
      this.patientBenefitForm
        .get("financial")
        .get("deductible")
        .setValue(
          Number(
            this.patientBenefitForm.get("financial").get("deductible").value
          )
        );
    } else {
      this.patientBenefitForm.get("financial").get("deductible").setValue(null);
    }

    if (
      this.patientBenefitForm.get("financial").get("maxVisit").value !== null &&
      this.patientBenefitForm.get("financial").get("maxVisit").value !== ""
    ) {
      this.patientBenefitForm
        .get("financial")
        .get("maxVisit")
        .setValue(
          Number(this.patientBenefitForm.get("financial").get("maxVisit").value)
        );
    } else {
      this.patientBenefitForm.get("financial").get("maxVisit").setValue(null);
    }

    if (this.patientBenefitForm.get("benefitType").value === HOSPICE) {
      if (
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("routineHomeCareHighDaysUsed").value !== null &&
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("routineHomeCareHighDaysUsed").value !== ""
      ) {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("routineHomeCareHighDaysUsed")
          .setValue(
            Number(
              this.patientBenefitForm
                .get("hospiceEligibility")
                .get("routineHomeCareHighDaysUsed").value
            ),
            { emitEvent: false }
          );
      } else {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("routineHomeCareHighDaysUsed")
          .setValue(null, { emitEvent: false });
      }

      if (
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("hospiceBenefitElectionDate").value !== ""
      ) {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("hospiceBenefitElectionDate")
          .setValue(
            this.formatDate(
              this.patientBenefitForm
                .get("hospiceEligibility")
                .get("hospiceBenefitElectionDate").value
            ),
            { emitEvent: false }
          );
      }

      if (
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("admitBenefitPeriod").value !== null &&
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("admitBenefitPeriod").value !== ""
      ) {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("admitBenefitPeriod")
          .setValue(
            Number(
              this.patientBenefitForm
                .get("hospiceEligibility")
                .get("admitBenefitPeriod").value
            ),
            { emitEvent: false }
          );
      } else {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("admitBenefitPeriod")
          .setValue(null);
      }

      if (
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("admitBenefitPeriodStartDate").value !== ""
      ) {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("admitBenefitPeriodStartDate")
          .setValue(
            this.formatDate(
              this.patientBenefitForm
                .get("hospiceEligibility")
                .get("admitBenefitPeriodStartDate").value
            ),
            { emitEvent: false }
          );
      }

      if (
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("previousHospiceStartDate").value !== ""
      ) {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("previousHospiceStartDate")
          .setValue(
            this.formatDate(
              this.patientBenefitForm
                .get("hospiceEligibility")
                .get("previousHospiceStartDate").value
            )
          );
      }

      if (
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("previousHospiceDischargeDate").value !== ""
      ) {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("previousHospiceDischargeDate")
          .setValue(
            this.formatDate(
              this.patientBenefitForm
                .get("hospiceEligibility")
                .get("previousHospiceDischargeDate").value
            )
          );
      }

      if (
        this.patientBenefitForm.get("hospiceEligibility").get("dateOfFinalBill")
          .value !== ""
      ) {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("dateOfFinalBill")
          .setValue(
            this.formatDate(
              this.patientBenefitForm
                .get("hospiceEligibility")
                .get("dateOfFinalBill").value
            )
          );
      }

      if (
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("noticeAcceptedDate").value !== ""
      ) {
        this.patientBenefitForm
          .get("hospiceEligibility")
          .get("noticeAcceptedDate")
          .setValue(
            this.formatDate(
              this.patientBenefitForm
                .get("hospiceEligibility")
                .get("noticeAcceptedDate").value
            )
          );
      }

      this.liabilities.controls.forEach((liability) => {
        if (liability.value.liabilityAmount !== "") {
          liability.value.liabilityAmount = Number(
            liability.value.liabilityAmount
          );
        }

        if (liability.value.fromDate !== "") {
          liability.value.fromDate = this.formatDate(liability.value.fromDate);
        }

        if (liability.value.toDate !== "") {
          liability.value.toDate = this.formatDate(liability.value.toDate);
        }
      });

      if (
        this.roomAndBoardEligibility.value.admitBenefitPeriod &&
        this.roomAndBoardEligibility.value.admitBenefitPeriod !== ""
      ) {
        this.roomAndBoardEligibility.value.admitBenefitPeriod = Number(
          this.roomAndBoardEligibility.value.admitBenefitPeriod
        );
      }

      if (
        this.roomAndBoardEligibility.value.admitBenefitPeriodStartDate !== ""
      ) {
        this.roomAndBoardEligibility.value.admitBenefitPeriodStartDate =
          this.formatDate(
            this.roomAndBoardEligibility.value.admitBenefitPeriodStartDate
          );
      }
    }

    this.authorizations.controls.forEach((c) => {
      if (
        c.get("authorizationValue").value !== null &&
        c.get("authorizationValue").value !== ""
      ) {
        c.get("authorizationValue").setValue(
          Number(c.get("authorizationValue").value),
          { emitEvent: false }
        );
      } else {
        c.get("authorizationValue").setValue(null);
      }
      if (
        c.get("quantityAuthorized").value !== null &&
        c.get("quantityAuthorized").value !== ""
      ) {
        c.get("quantityAuthorized").setValue(
          Number(c.get("quantityAuthorized").value),
          { emitEvent: false }
        );
      } else {
        c.get("quantityAuthorized").setValue(null);
      }

      c.get("authorizationStart").setValue(
        this.formatDate(c.get("authorizationStart").value),
        { emitEvent: false }
      );
      c.get("authorizationEnd").setValue(
        this.formatDate(c.get("authorizationEnd").value),
        { emitEvent: false }
      );
    });
    this.impactToClaim = oldImpactToClaim;
  }

  cancel() {
    this.clearForm();
  }

  clearForm() {
    this.patientBenefitForm.reset({}, { emitEvent: false });
    this.selectedPayerName = null;
    // this.selectedPayerSubtype = null;
    this.selectedState = null;
    this.showForm.emit(false);
    this.reloadGrid.emit(true);
  }

  onFailure(msg: string) {
    this.utilService.decreaseSpinnerCount();
    const alert = this.alertCtrl.create({
      title: "Error Message",
      message: msg,
      buttons: [{ text: "OK" }],
    });
    alert.present();
  }

  allowOnlyInteger(e) {
    if ([69, 107, 109, 110, 190, 187, 189].includes(e.keyCode)) {
      e.preventDefault();
    }
    this.preventCopyPaste(e);
  }

  allowOnlyDecimal(e) {
    if ([69, 107, 109, 187, 189].includes(e.keyCode)) {
      e.preventDefault();
    }
    this.preventCopyPaste(e);
  }
  allowOnlyNumbersAndLetters(e) {
    var regex = new RegExp("^[a-zA-Z0-9-]+$");
    var str = !e.key ? String.fromCharCode(e.wich) : e.key;
    if (!regex.test(str)) {
      e.preventDefault();
    }
  }

  preventCopyPaste(e) {
    if (e.ctrlKey && e.keyCode == 86) {
      return false;
    }
  }

  applyMask(value, mask) {
    if (!value) {
      return value;
    }
    let values = value.split("");
    let lastIndex;
    lastIndex = 0;
    mask = mask.split("");
    mask.forEach((val, index) => {
      if (commonVar.SPECIAL_CHAR.indexOf(val) === -1) {
        mask[index] = values[lastIndex];
        lastIndex += 1;
      }
    });
    return mask.join("");
  }

  payerLevelChanged(val: Config) {
    this.selectedPayerLevel = val;
    this.payerTypeList = payerTypeList;
    this.patientBenefitForm.get("payerType").setValue(null);
    this.patientBenefitForm.get("payerName").setValue(null);
    this.payerNameOptions = [];
    if (val.value == "ROOMANDBOARD") {
      this.payerTypeList = this.payerTypeList.filter(
        (x) => x.value == "MEDICAID" || x.value == "COMMERCIAL"
      );
    }
  }
  isPayerOverlap(): boolean {
    let isOverlapped = false;
    let currentDate = this.formatDate(moment());

    let benefitsList = this.patientBenefitsList.filter((p) => {
      if (
        p.expiredDate === "" ||
        moment(p.expiredDate).isSameOrAfter(moment(currentDate))
      ) {
        return p;
      }
    });

    if (benefitsList.length > 0) {
      let payerId = this.patientBenefitForm.get("payerId").value;
      let payerType = this.patientBenefitForm.get("payerType").value;
      let payerName = this.patientBenefitForm.get("payerName").value;
      let effectiveDate = this.patientBenefitForm.get("effectiveDate").value;
      let expiredDate = this.patientBenefitForm.get("expiredDate").value;
      let payerPrimacyLevel =
        this.patientBenefitForm.get("payerPrimacyLevel").value;

      if (
        payerId !== "" &&
        payerName !== "" &&
        payerType !== "" &&
        effectiveDate !== "" &&
        payerPrimacyLevel !== ""
      ) {
        if (this.editMode) {
          if (
            benefitsList.length === 1 &&
            benefitsList.find(
              (f) => f.benefitsId === this.currentPatientBenefit.benefitsId
            )
          ) {
            return false;
          }

          if (
            this.currentPatientBenefit.effectiveDate === effectiveDate &&
            this.currentPatientBenefit.expiredDate === expiredDate &&
            this.currentPatientBenefit.payerId === payerId &&
            this.currentPatientBenefit.payerType === payerType &&
            this.currentPatientBenefit.payerName === payerName
          ) {
            return false;
          }
        }

        benefitsList = this.patientBenefitsList.filter((p) => {
          if (
            p.payerId === payerId &&
            p.payerName === payerName &&
            p.payerType === payerType &&
            p.payerPrimacyLevel !== payerPrimacyLevel
          ) {
            if (!this.editMode) {
              return p;
            } else if (
              this.editMode &&
              p.benefitsId !== this.currentPatientBenefit.benefitsId
            ) {
              return p;
            }
          }
        });

        console.log("benefitsList", benefitsList);

        if (benefitsList.length <= 0) {
          return false;
        }

        benefitsList.forEach((b) => {
          if (!isOverlapped) {
            if (
              b.expiredDate !== "" &&
              expiredDate !== "" &&
              moment(effectiveDate).isSameOrBefore(b.expiredDate) &&
              moment(expiredDate).isSameOrAfter(b.effectiveDate)
            ) {
              console.log("1. affected benefit effectiveDate", b.effectiveDate);
              console.log("1. affected benefit expiredDate", b.expiredDate);
              this.showPayerOverlapErrorMessage(b.payerPrimacyLevel);
              isOverlapped = true;
              return true;
            } else if (
              b.expiredDate !== "" &&
              expiredDate === "" &&
              moment(effectiveDate).isSameOrBefore(b.expiredDate)
            ) {
              console.log("2. affected benefit effectiveDate", b.effectiveDate);
              console.log("2. affected benefit expiredDate", b.expiredDate);
              this.showPayerOverlapErrorMessage(b.payerPrimacyLevel);
              isOverlapped = true;
              return true;
            } else if (
              b.expiredDate === "" &&
              expiredDate !== "" &&
              moment(expiredDate).isSameOrAfter(b.effectiveDate)
            ) {
              console.log("3. affected benefit effectiveDate", b.effectiveDate);
              console.log("3. affected benefit expiredDate", b.expiredDate);
              this.showPayerOverlapErrorMessage(b.payerPrimacyLevel);
              isOverlapped = true;
              return true;
            } else if (b.expiredDate === "" && expiredDate === "") {
              console.log("4. affected benefit effectiveDate", b.effectiveDate);
              console.log("4. affected benefit expiredDate", b.expiredDate);
              this.showPayerOverlapErrorMessage(b.payerPrimacyLevel);
              isOverlapped = true;
              return true;
            }
          }
        });
      }
    }

    return isOverlapped;
  }

  showPayerOverlapErrorMessage(payerPrimacyLevel: string) {
    return this.onFailure(
      "This payer is already in effect as " +
        payerPrimacyLevel +
        ". Please address that level first."
    );
  }

  updatePatientEligibilityVerified() {
    this.patientBenefitForm
      .get("eligibilityVerified")
      .setValue(this.patientEligibilityVerified);
  }

  updateIsVBID() {
    if (this.isBVID) {
      this.subscriberHistory.controls.forEach((s) => {
        s.get("subscriberId").clearValidators();
        s.get("subscriberId").setErrors(null);
      });
    } else if (this.patientBenefitForm.get("payerType").value === "MEDICARE") {
      this.subscriberHistory.controls.forEach((s) => {
        s.get("subscriberId").setValidators(
          Validators.compose([
            Validators.pattern(this.subscriberIdPattern),
            Validators.maxLength(11),
          ])
        );
        s.get("subscriberId").setValue(
          s.get("subscriberId").value.toString().substring(0, 11)
        );
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/patient-benefits/patient-benefits-form/patient-benefits-form.ts